import React, { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import ReactApexChart from "react-apexcharts";
import {
  Form, Input, Select, Button, Alert, Card, Spin,
  Table, Tag, Modal, Tooltip, Divider, Tabs, Upload, Progress,
  Switch, InputNumber, Popconfirm, Statistic, Row, Col, Radio, DatePicker, message,
} from "antd";
import {
  MailOutlined, TeamOutlined, GlobalOutlined,
  BankOutlined, EyeOutlined, ReloadOutlined, CopyOutlined,
  FileExcelOutlined, DownloadOutlined, CheckCircleOutlined,
  CloseCircleOutlined, UploadOutlined, PlusOutlined, DeleteOutlined,
  StarOutlined, ClockCircleOutlined, StopOutlined, ThunderboltOutlined,
  BarChartOutlined, WarningOutlined, SendOutlined, MinusCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import api from "../../../core/axios/axiosInstance";

dayjs.extend(relativeTime);

const { TextArea } = Input;

// ── Audience options ──────────────────────────────────────────────────────────
const TARGET_OPTIONS = [
  {
    value: "excel",
    label: "Send via Excel File",
    description: "Upload an Excel/CSV file — valid emails are extracted and sent",
    icon: <FileExcelOutlined style={{ fontSize: 20, color: "#16a34a" }} />,
    color: "#dcfce7", border: "#16a34a", tagColor: "green",
  },
  {
    value: "all",
    label: "Send to Everyone",
    description: "All company admins and all agents across every company",
    icon: <GlobalOutlined style={{ fontSize: 20, color: "#6366f1" }} />,
    color: "#ede9fe", border: "#6366f1", tagColor: "purple",
  },
  {
    value: "companies",
    label: "Send to Company Admins",
    description: "Only company admin accounts (not agents)",
    icon: <BankOutlined style={{ fontSize: 20, color: "#0ea5e9" }} />,
    color: "#e0f2fe", border: "#0ea5e9", tagColor: "blue",
  },
  {
    value: "specific",
    label: "Send to Specific Companies",
    description: "Select one or more companies — only their admins receive this",
    icon: <TeamOutlined style={{ fontSize: 20, color: "#f59e0b" }} />,
    color: "#fef3c7", border: "#f59e0b", tagColor: "orange",
  },
];

const targetLabel = (t) => TARGET_OPTIONS.find((o) => o.value === t);

function formatDuration(totalSeconds) {
  if (!totalSeconds || totalSeconds <= 0) return "Instant";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s && !h) parts.push(`${s}s`);
  return parts.join(" ") || "Instant";
}

// ── HTML preview using a sandboxed iframe ─────────────────────────────────────
// Pass `fullHtml` to render a complete HTML document as-is (e.g. full email template).
// Pass `html` + optional `title`/`subject` for the simple body-only mode.
function HtmlPreview({ html, fullHtml, title, subject }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;
    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    const content = fullHtml || `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #222; font-size: 14px; line-height: 1.7; }
    img  { max-width: 100%; }
  </style>
</head>
<body>
  ${subject ? `<div style="font-size:11px;color:#888;margin-bottom:8px;">Subject: <strong>${subject}</strong></div>` : ""}
  ${title   ? `<h2 style="margin:0 0 16px;color:#111;font-size:20px;">${title}</h2>` : ""}
  ${html || "<em style='color:#aaa'>No body content</em>"}
</body>
</html>`;
    doc.open();
    doc.write(content);
    doc.close();
    const resize = () => {
      try {
        const h = doc.body.scrollHeight;
        iframeRef.current.style.height = `${Math.max(h + 20, 80)}px`;
      } catch (_) {}
    };
    setTimeout(resize, 80);
  }, [html, fullHtml, title, subject]);

  return (
    <iframe
      ref={iframeRef}
      title="email-preview"
      sandbox="allow-same-origin"
      style={{ width: "100%", border: "none", minHeight: 80, display: "block" }}
    />
  );
}

// ── Batch job detail modal ────────────────────────────────────────────────────
function BatchDetailModal({ job, onClose, onUseAsTemplate }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [batchFilter, setBatchFilter] = useState("all");

  useEffect(() => {
    if (!job) return;
    setDetail(null);
    setLoading(true);
    // Try API first; if it fails, fall back to data already in the job object
    api.get(`/notifications/batch-jobs/${job.jobId}`)
      .then((r) => setDetail(r.data?.data || null))
      .catch(() => {
        // Fallback: build detail from list data (batches.recipients already loaded)
        const recipients = (job.batches || []).flatMap((b) => b.recipients || []);
        setDetail({
          ...job,
          recipients,
          recipientCount: recipients.length || job.totalRecipients || 0,
        });
      })
      .finally(() => setLoading(false));
  }, [job]);

  // Use detail.recipients directly — the API already attaches events (Delivery, Open, Bounce, Click)
  // and all batch metadata (batchIndex, batchStatus, sentAt, scheduledAt) to each recipient.
  // Previously this rebuilt the list from detail.batches[].recipients[] which has NO events
  // attached, causing Delivered / Opened / Bounced columns to always show "—".
  const flatRecipients = detail?.recipients?.length
    ? detail.recipients
    : (detail?.batches || []).flatMap((batch) =>
        (batch.recipients || []).map((r) => ({
          ...r,
          batchIndex:   batch.index,
          batchStatus:  batch.status,
          scheduledAt:  batch.scheduledAt,
          sentAt:       batch.sentAt,
          batchError:   batch.error,
          events:       {},
        }))
      );

  const sentCount    = flatRecipients.filter((r) => r.batchStatus === "sent").length;
  const failedCount  = flatRecipients.filter((r) => r.batchStatus === "failed").length;
  const pendingCount = flatRecipients.filter((r) => r.batchStatus === "pending").length;

  const recipientCols = [
    {
      title: "Name / Email",
      key: "name",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#374151" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Batch",
      key: "batch",
      width: 70,
      render: (_, r) => (
        <Tooltip title={
          r.batchStatus === "sent"   ? `Sent at ${r.sentAt ? dayjs(r.sentAt).format("HH:mm DD MMM") : "—"}` :
          r.batchStatus === "failed" ? (r.batchError || "Send failed") :
          `Scheduled for ${r.scheduledAt ? dayjs(r.scheduledAt).format("HH:mm DD MMM") : "—"}`
        }>
          <span style={{ fontSize: 12, color: "#6b7280", cursor: "help" }}>
            #{r.batchIndex + 1}
            {" "}
            {r.batchStatus === "sent"   ? <Tag color="success" style={{ margin: 0, fontSize: 10 }}>Sent</Tag>
            : r.batchStatus === "failed" ? <Tag color="error"   style={{ margin: 0, fontSize: 10 }}>Failed</Tag>
            : <Tag color="warning" style={{ margin: 0, fontSize: 10 }}>Pending</Tag>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Delivered",
      key: "delivered",
      align: "center",
      render: (_, r) => r.events?.Delivery ? (
        <Tooltip title={dayjs(r.events.Delivery).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="green" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
    {
      title: "Opened",
      key: "opened",
      align: "center",
      render: (_, r) => r.events?.Open ? (
        <Tooltip title={dayjs(r.events.Open).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="blue" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
    {
      title: "Bounced",
      key: "bounced",
      align: "center",
      render: (_, r) => r.events?.Bounce ? (
        <Tooltip title={dayjs(r.events.Bounce).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="orange" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
  ];

  return (
    <Modal
      title={null}
      open={!!job}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Tooltip title="Pre-fill the compose form with this email's subject, heading, and body">
              <Button
                icon={<CopyOutlined />}
                style={{ borderRadius: 8 }}
                disabled={!detail}
                onClick={() => {
                  if (detail) { onUseAsTemplate(detail); onClose(); }
                }}
              >
                Use as Template
              </Button>
            </Tooltip>
            {/* FIX 8B: Download failed recipients — emails never sent */}
            {failedCount > 0 && detail && (
              <Tooltip title="Download recipients whose batch failed — these emails were never sent">
                <Button
                  icon={<DownloadOutlined />}
                  style={{ borderRadius: 8, borderColor: "#ef4444", color: "#ef4444" }}
                  onClick={() => {
                    const failedRecipients = flatRecipients.filter((r) => r.batchStatus === "failed");
                    const rows = failedRecipients.map((r) => ({
                      Name:        r.name  || "",
                      Email:       r.email || "",
                      "Batch #":   (r.batchIndex ?? 0) + 1,
                      "Error":     r.batchError || "Batch failed",
                      "Scheduled": r.scheduledAt ? dayjs(r.scheduledAt).format("DD MMM YYYY HH:mm") : "",
                    }));
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Failed Recipients");
                    XLSX.writeFile(wb, `failed_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
                  }}
                >
                  Download Failed ({failedCount})
                </Button>
              </Tooltip>
            )}
          </div>
          <Button onClick={onClose} style={{ borderRadius: 8 }}>Close</Button>
        </div>
      }
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 20px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <MailOutlined style={{ marginRight: 8 }} />Email Detail
        </h5>
        {job && (
          <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>
            {dayjs(job.createdAt).format("DD MMM YYYY [at] HH:mm")}
          </p>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
      ) : detail ? (
        <Tabs
          size="small"
          items={[
            {
              key: "preview",
              label: <span><EyeOutlined style={{ marginRight: 4 }} />Preview</span>,
              children: (
                <>
                  <div style={{
                    background: "#f9fafb", border: "1px solid #e5e7eb",
                    borderRadius: 10, padding: "12px 16px", marginBottom: 14,
                  }}>
                    {[
                      { label: "Subject",      value: detail.subject,                                                              bold: true },
                      { label: "Heading",      value: detail.title || "—",                                                         html: true },
                      { label: "Status",       value: detail.status?.toUpperCase() },
                      { label: "From",         value: detail.fromEmail ? `${detail.fromName || "VOYCELL"} <${detail.fromEmail}>` : "noreply@voycell.com" },
                      { label: "Recipients",   value: `${detail.recipientCount} recipient${detail.recipientCount !== 1 ? "s" : ""}${detail.droppedCount > 0 ? ` (${detail.droppedCount} dropped)` : ""}` },
                      { label: "Batches",        value: `${detail.completedBatches} / ${detail.totalBatches} completed` },
                      { label: "Batch size",     value: `${detail.batchSize} email${detail.batchSize !== 1 ? "s" : ""} per batch` },
                      { label: "Interval",       value: `${detail.intervalValue} ${detail.intervalUnit} between batches` },
                      ...(detail.totalBatches > 1 ? [{ label: "Total duration", value: formatDuration((detail.totalBatches - 1) * detail.intervalSeconds) }] : []),
                      ...((() => {
                        const batches = detail.batches || [];
                        const first   = batches[0];
                        const last    = batches[batches.length - 1];
                        const fmt = (d) => dayjs(d).format("DD MMM YYYY [at] hh:mm A");
                        const rows = [];

                        // Use scheduledStartAt (user's original intent) when available;
                        // fall back to the batch heuristic for legacy jobs.
                        if (detail.scheduledStartAt != null) {
                          rows.push({
                            label: "Scheduled",
                            value: detail.scheduledStartAt
                              ? `Batch 1 at ${fmt(detail.scheduledStartAt)}`
                              : "Immediately",
                          });
                        } else if (first) {
                          const schedAt = first.scheduledAt || first.sentAt;
                          if (schedAt) {
                            const diffMs = new Date(schedAt) - new Date(detail.createdAt);
                            rows.push({
                              label: "Scheduled",
                              value: diffMs <= 2 * 60_000 ? "Immediately" : `Batch 1 at ${fmt(first.scheduledAt)}`,
                            });
                          }
                        }

                        // Actual send time of first batch
                        if (first?.sentAt) {
                          rows.push({ label: "First batch sent", value: fmt(first.sentAt) });
                        }

                        // Estimated or actual completion (last batch)
                        if (last && first && last.index !== first.index) {
                          if (last.sentAt) {
                            rows.push({ label: "Completed at", value: fmt(last.sentAt) });
                          } else if (last.scheduledAt) {
                            rows.push({ label: "Est. completion", value: `Last batch at ${fmt(last.scheduledAt)}` });
                          }
                        }

                        return rows;
                      })()),
                      ...(detail.replyTo ? [{ label: "Reply-To", value: detail.replyTo }] : []),
                      ...(detail.attachments?.length ? [{ label: "Attachments", value: detail.attachments.map((a) => a.filename).join(", ") }] : []),
                    ].map(({ label, value, bold, html }) => (
                      <div key={label} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: "#6b7280", minWidth: 72, paddingTop: 1 }}>{label}</span>
                        {html
                          ? <span style={{ fontSize: 13, color: "#111827" }} dangerouslySetInnerHTML={{ __html: value }} />
                          : <span style={{ fontSize: 13, color: "#111827", fontWeight: bold ? 700 : 400 }}>{value}</span>
                        }
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email Preview</div>
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff", maxHeight: 340, overflowY: "auto" }}>
                    <HtmlPreview html={detail.body} title={detail.title} subject={detail.subject} />
                  </div>
                </>
              ),
            },
            {
              key: "html",
              label: <span><CopyOutlined style={{ marginRight: 4 }} />Raw HTML</span>,
              children: (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Subject</div>
                    <pre style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{detail.subject}</pre>
                  </div>
                  {detail.title && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Heading (HTML)</div>
                      <pre style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{detail.title}</pre>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Body (HTML)</div>
                    <pre style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 300, overflowY: "auto" }}>{detail.body}</pre>
                  </div>
                </>
              ),
            },
            {
              key: "stats",
              label: <span><BarChartOutlined style={{ marginRight: 4 }} />Statistics</span>,
              children: (() => {
                const s     = detail?.stats || {};
                const total = detail?.totalRecipients || detail?.recipientCount || 1;
                const rows  = [
                  { label: "Delivered",  value: s.netDeliveries ?? s.deliveries ?? 0, color: "#15803d", bg: "#dcfce7" },
                  { label: "Opened",     value: s.opens       || 0, color: "#1d4ed8", bg: "#dbeafe" },
                  { label: "Clicked",    value: s.clicks      || 0, color: "#7c3aed", bg: "#ede9fe" },
                  { label: "Bounced",    value: s.bounces     || 0, color: "#b45309", bg: "#fef3c7" },
                  { label: "Complaint",  value: s.complaints  || 0, color: "#b91c1c", bg: "#fee2e2" },
                  { label: "Rejected",   value: s.rejections  || 0, color: "#6b7280", bg: "#f3f4f6" },
                ];
                const hasAny = rows.some((r) => r.value > 0);
                const chartOptions = {
                  chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
                  colors: rows.map((r) => r.color),
                  plotOptions: { bar: { borderRadius: 6, columnWidth: "50%", distributed: true } },
                  dataLabels: {
                    enabled: true,
                    formatter: (v) => v > 0 ? v : "",
                    style: { fontSize: "12px", fontWeight: 700 },
                  },
                  xaxis: {
                    categories: rows.map((r) => r.label),
                    labels: { style: { fontSize: "12px", colors: "#374151" } },
                  },
                  yaxis: {
                    labels: { formatter: (v) => Math.round(v) },
                    title: { text: "Count", style: { fontSize: "11px", color: "#6b7280" } },
                  },
                  tooltip: {
                    y: { formatter: (v) => `${v} (${total > 0 ? Math.round((v / total) * 100) : 0}%)` },
                  },
                  legend: { show: false },
                  grid: { borderColor: "#f3f4f6" },
                };
                return (
                  <div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                      {rows.map((r) => (
                        <div key={r.label} style={{
                          flex: "1 1 80px", textAlign: "center",
                          background: r.bg, borderRadius: 10, padding: "10px 8px",
                        }}>
                          <div style={{ fontWeight: 700, fontSize: 18, color: r.color }}>{r.value}</div>
                          <div style={{ fontSize: 11, color: r.color, opacity: 0.8 }}>{r.label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>
                            {total > 0 ? Math.round((r.value / total) * 100) : 0}%
                          </div>
                        </div>
                      ))}
                    </div>
                    {hasAny ? (
                      <ReactApexChart
                        type="bar"
                        height={220}
                        options={chartOptions}
                        series={[{ name: "Count", data: rows.map((r) => r.value) }]}
                      />
                    ) : (
                      <div style={{ textAlign: "center", padding: "30px 0", color: "#9ca3af", fontSize: 13 }}>
                        No tracking data yet — stats will populate as emails are delivered.
                      </div>
                    )}
                  </div>
                );
              })(),
            },
            {
              key: "recipients",
              label: `Recipients (${detail.recipientCount})`,
              children: (() => {
                const displayed = batchFilter === "sent"    ? flatRecipients.filter((r) => r.batchStatus === "sent")
                                : batchFilter === "pending" ? flatRecipients.filter((r) => r.batchStatus === "pending")
                                : batchFilter === "failed"  ? flatRecipients.filter((r) => r.batchStatus === "failed")
                                : flatRecipients;
                return (
                  <>
                    {/* Summary strip + filter */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "stretch" }}>
                      {[
                        { key: "sent",    label: "Sent",    count: sentCount,            color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
                        { key: "pending", label: "Pending", count: pendingCount,          color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
                        { key: "failed",  label: "Failed",  count: failedCount,           color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
                        { key: "all",     label: "Total",   count: flatRecipients.length, color: "#4f46e5", bg: "#f8faff", border: "#e0e7ff" },
                      ].map(({ key, label, count, color, bg, border }) => (
                        <div
                          key={key}
                          onClick={() => setBatchFilter(key)}
                          style={{
                            flex: 1, minWidth: 80, textAlign: "center",
                            background: bg, border: `2px solid ${batchFilter === key ? color : border}`,
                            borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                            transition: "border-color 0.15s",
                          }}
                        >
                          <div style={{ fontSize: 22, fontWeight: 700, color }}>{count}</div>
                          <div style={{ fontSize: 12, color }}>{label}</div>
                        </div>
                      ))}
                    </div>

                    <Table
                      dataSource={displayed}
                      columns={recipientCols}
                      rowKey={(r) => `${r.email}-${r.batchIndex}`}
                      size="small"
                      scroll={{ x: "max-content", y: 320 }}
                      pagination={{ pageSize: 10, size: "small", showSizeChanger: true, pageSizeOptions: ["10","20","50"] }}
                      locale={{ emptyText: "No recipients found" }}
                      rowClassName={(r) =>
                        r.batchStatus === "sent"   ? "" :
                        r.batchStatus === "failed" ? "ant-table-row-danger" : ""
                      }
                    />
                  </>
                );
              })(),
            },
          ]}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
          Failed to load batch detail.
        </div>
      )}
    </Modal>
  );
}

// ── Email detail modal ─────────────────────────────────────────────────────────
function EmailDetailModal({ log, onClose, onUseAsTemplate, onStatsRefreshed, onTargetRecipients }) {
  const [detail, setDetail]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);

  // Paginated recipients state
  const [recipients,      setRecipients]      = useState([]);
  const [recipientTotal,  setRecipientTotal]  = useState(0);
  const [recipientPage,   setRecipientPage]   = useState(1);
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [recipientLoading, setRecipientLoading] = useState(false);
  const RECIPIENT_PAGE_SIZE = 20;

  const fetchRecipients = useCallback((id, page, filter) => {
    setRecipientLoading(true);
    api.get(`/notifications/email-logs/${id}/recipients`, {
      params: { page, limit: RECIPIENT_PAGE_SIZE, filter },
    })
      .then((r) => {
        setRecipients(r.data?.data || []);
        setRecipientTotal(r.data?.total || 0);
      })
      .catch(() => {})
      .finally(() => setRecipientLoading(false));
  }, []);

  // FIX 8C: each download type uses only its own relevant columns.
  // "all" includes every status column.
  const downloadFilteredRecipients = (id, filter, filename) => {
    // "undelivered" = no events at all — fetch all then filter client-side
    const apiFilter = filter === "undelivered" ? "all" : filter;
    api.get(`/notifications/email-logs/${id}/recipients`, {
      params: { download: "true", filter: apiFilter },
    }).then((r) => {
      let data = r.data?.data || [];
      if (filter === "undelivered") {
        data = data.filter((rec) => !rec.events?.Delivery && !rec.events?.Bounce && !rec.events?.Open && !rec.events?.Click);
      }
      let rows;
      if (filter === "delivered") {
        rows = data.map((rec) => ({
          Name:             rec.name  || "",
          Email:            rec.email || "",
          "Delivered Time": rec.events?.Delivery ? dayjs(rec.events.Delivery).format("DD MMM YYYY HH:mm:ss") : "",
        }));
      } else if (filter === "opened") {
        rows = data.map((rec) => ({
          Name:          rec.name  || "",
          Email:         rec.email || "",
          "Opened Time": rec.events?.Open ? dayjs(rec.events.Open).format("DD MMM YYYY HH:mm:ss") : "",
        }));
      } else if (filter === "bounced") {
        rows = data.map((rec) => ({
          Name:          rec.name  || "",
          Email:         rec.email || "",
          "Bounce Time": rec.events?.Bounce ? dayjs(rec.events.Bounce).format("DD MMM YYYY HH:mm:ss") : "",
        }));
      } else if (filter === "undelivered") {
        rows = data.map((rec) => ({
          Name:  rec.name  || "",
          Email: rec.email || "",
        }));
      } else {
        // "all" — every status column
        rows = data.map((rec) => ({
          Name:             rec.name  || "",
          Email:            rec.email || "",
          "Delivered":      rec.events?.Delivery ? "Yes" : "No",
          "Delivered Time": rec.events?.Delivery ? dayjs(rec.events.Delivery).format("DD MMM YYYY HH:mm:ss") : "",
          "Opened":         rec.events?.Open      ? "Yes" : "No",
          "Opened Time":    rec.events?.Open      ? dayjs(rec.events.Open).format("DD MMM YYYY HH:mm:ss")      : "",
          "Bounced":        rec.events?.Bounce    ? "Yes" : "No",
          "Bounce Time":    rec.events?.Bounce    ? dayjs(rec.events.Bounce).format("DD MMM YYYY HH:mm:ss")    : "",
        }));
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Recipients");
      XLSX.writeFile(wb, filename);
    }).catch(() => {});
  };

  const fetchDetail = (id) => {
    setLoading(true);
    api.get(`/notifications/email-logs/${id}`)
      .then((r) => setDetail(r.data?.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!log) return;
    setDetail(null);
    setRecipients([]);
    setRecipientPage(1);
    setRecipientFilter("all");
    fetchDetail(log._id);
    fetchRecipients(log._id, 1, "all");
  }, [log, fetchRecipients]);

  const handleSyncStats = () => {
    if (!log?._id) return;
    setSyncing(true);
    api.get(`/notifications/email-logs/${log._id}/sync-stats`)
      .then((r) => {
        setDetail((prev) => prev ? { ...prev, stats: r.data?.stats } : prev);
        if (onStatsRefreshed) onStatsRefreshed();
      })
      .catch(() => {})
      .finally(() => setSyncing(false));
  };

  const recipientCols = [
    {
      title: "Name / Email",
      key: "name",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || "—"}</div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Delivered",
      key: "delivered",
      align: "center",
      render: (_, r) => r.events?.Delivery ? (
        <Tooltip title={dayjs(r.events.Delivery).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="green" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
    {
      title: "Opened",
      key: "opened",
      align: "center",
      render: (_, r) => r.events?.Open ? (
        <Tooltip title={dayjs(r.events.Open).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="blue" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
    {
      title: "Clicked",
      key: "clicked",
      align: "center",
      render: (_, r) => r.events?.Click ? (
        <Tooltip title={dayjs(r.events.Click).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="purple" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
    {
      title: "Bounced",
      key: "bounced",
      align: "center",
      render: (_, r) => r.events?.Bounce ? (
        <Tooltip title={dayjs(r.events.Bounce).format("DD MMM YYYY HH:mm:ss")}>
          <Tag color="orange" style={{ borderRadius: 20, fontSize: 13, margin: 0, cursor: "default" }}>✓</Tag>
        </Tooltip>
      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>,
    },
  ];

  return (
    <Modal
      title={null}
      open={!!log}
      onCancel={onClose}
      width={860}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Tooltip title="Pre-fill the compose form with this email's subject, heading, and body">
            <Button
              icon={<CopyOutlined />}
              style={{ borderRadius: 8 }}
              disabled={!detail}
              onClick={() => {
                if (detail) { onUseAsTemplate(detail); onClose(); }
              }}
            >
              Use as Template
            </Button>
          </Tooltip>
          <Button onClick={onClose} style={{ borderRadius: 8 }}>Close</Button>
        </div>
      }
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 20px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <MailOutlined style={{ marginRight: 8 }} />Sent Email Detail
        </h5>
        {log && (
          <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>
            {dayjs(log.createdAt).format("DD MMM YYYY [at] HH:mm")}
          </p>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
      ) : detail ? (
        <Tabs
          size="small"
          items={[
            {
              key: "preview",
              label: <span><EyeOutlined style={{ marginRight: 4 }} />Preview</span>,
              children: (
                <>
                  {/* Meta row */}
                  <div style={{
                    background: "#f9fafb", border: "1px solid #e5e7eb",
                    borderRadius: 10, padding: "12px 16px", marginBottom: 14,
                  }}>
                    {[
                      { label: "Subject",    value: detail.subject,                            bold: true },
                      { label: "Heading",    value: detail.title || "—",                       html: true },
                      { label: "From",       value: detail.fromEmail ? `${detail.fromName || "VOYCELL"} <${detail.fromEmail}>` : "noreply@voycell.com" },
                      { label: "Target",     value: null,                                      tag: detail.target },
                      { label: "Recipients", value: `${detail.recipientCount} recipient${detail.recipientCount !== 1 ? "s" : ""}` },
                      ...(detail.batchJobId ? [
                        { label: "Batches",        value: `${detail.totalBatches} batches of ${detail.batchSize}` },
                        { label: "Interval",       value: `${detail.intervalValue} ${detail.intervalUnit} between batches` },
                        ...(detail.totalBatches > 1 ? [{ label: "Total duration", value: formatDuration((detail.totalBatches - 1) * detail.intervalSeconds) }] : []),
                        { label: "Scheduled",      value: detail.scheduledStartAt ? dayjs(detail.scheduledStartAt).format("DD MMM YYYY [at] hh:mm A") : "Immediately" },
                      ] : []),
                    ].map(({ label, value, bold, html, tag }) => (
                      <div key={label} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: "#6b7280", minWidth: 72, paddingTop: 1 }}>{label}</span>
                        {tag ? (
                          <Tag color={targetLabel(tag)?.tagColor} style={{ borderRadius: 20, margin: 0, fontSize: 12 }}>
                            {targetLabel(tag)?.label || tag}
                          </Tag>
                        ) : html ? (
                          <span
                            style={{ fontSize: 13, color: "#111827" }}
                            dangerouslySetInnerHTML={{ __html: value }}
                          />
                        ) : (
                          <span style={{ fontSize: 13, color: "#111827", fontWeight: bold ? 700 : 400 }}>
                            {value}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Rendered email preview */}
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                    Email Preview
                  </div>
                  <div style={{
                    border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
                    background: "#fff", maxHeight: 340, overflowY: "auto",
                  }}>
                    <HtmlPreview
                      html={detail.body}
                      title={detail.title}
                      subject={detail.subject}
                    />
                  </div>
                </>
              ),
            },
            {
              key: "html",
              label: <span><CopyOutlined style={{ marginRight: 4 }} />Raw HTML</span>,
              children: (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Subject</div>
                    <pre style={{
                      background: "#f9fafb", border: "1px solid #e5e7eb",
                      borderRadius: 8, padding: "10px 12px",
                      fontSize: 12, margin: 0, whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}>{detail.subject}</pre>
                  </div>
                  {detail.title && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Heading (HTML)</div>
                      <pre style={{
                        background: "#f9fafb", border: "1px solid #e5e7eb",
                        borderRadius: 8, padding: "10px 12px",
                        fontSize: 12, margin: 0, whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}>{detail.title}</pre>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Body (HTML)</div>
                    <pre style={{
                      background: "#f9fafb", border: "1px solid #e5e7eb",
                      borderRadius: 8, padding: "10px 12px",
                      fontSize: 12, margin: 0, whiteSpace: "pre-wrap",
                      wordBreak: "break-word", maxHeight: 300, overflowY: "auto",
                    }}>{detail.body}</pre>
                  </div>
                </>
              ),
            },
            {
              key: "stats",
              label: <span><BarChartOutlined style={{ marginRight: 4 }} />Statistics</span>,
              children: (() => {
                const s     = detail.stats || {};
                const total = detail.recipientCount || 1;
                // Compute ALL stats from per-recipient data so Statistics tab is always
                // consistent with the Recipients tab.  Aggregate stats (s.*) may count
                // events for emails not in log.recipients, causing discrepancies.
                const recs = detail.recipients || [];
                const fromRecs = (fn) => recs.length > 0 ? recs.filter(fn).length : null;
                const rows  = [
                  { label: "Delivered",  value: fromRecs((r) => r.events?.Delivery && !r.events?.Bounce) ?? (s.netDeliveries ?? s.deliveries ?? 0), color: "#15803d", bg: "#dcfce7" },
                  { label: "Opened",     value: fromRecs((r) => r.events?.Open)       ?? (s.opens      ?? 0), color: "#1d4ed8", bg: "#dbeafe" },
                  { label: "Clicked",    value: fromRecs((r) => r.events?.Click)      ?? (s.clicks     ?? 0), color: "#7c3aed", bg: "#ede9fe" },
                  { label: "Bounced",    value: fromRecs((r) => r.events?.Bounce)     ?? (s.bounces    ?? 0), color: "#b45309", bg: "#fef3c7" },
                  { label: "Complaint",  value: fromRecs((r) => r.events?.Complaint) ?? (s.complaints  ?? 0), color: "#b91c1c", bg: "#fee2e2" },
                  { label: "Rejected",   value: fromRecs((r) => r.events?.Reject)    ?? (s.rejections  ?? 0), color: "#6b7280", bg: "#f3f4f6" },
                ];
                const hasAny = rows.some((r) => r.value > 0);
                const chartOptions = {
                  chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
                  colors: rows.map((r) => r.color),
                  plotOptions: {
                    bar: { borderRadius: 6, columnWidth: "50%", distributed: true },
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: (v) => v > 0 ? v : "",
                    style: { fontSize: "12px", fontWeight: 700 },
                  },
                  xaxis: {
                    categories: rows.map((r) => r.label),
                    labels: { style: { fontSize: "12px", colors: "#374151" } },
                  },
                  yaxis: {
                    labels: { formatter: (v) => Math.round(v) },
                    title: { text: "Count", style: { fontSize: "11px", color: "#6b7280" } },
                  },
                  tooltip: {
                    y: {
                      formatter: (v) => {
                        const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                        return `${v} (${pct}%)`;
                      },
                    },
                  },
                  legend: { show: false },
                  grid: { borderColor: "#f3f4f6" },
                };
                return (
                  <div>
                    {/* Sync button */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={syncing}
                        onClick={handleSyncStats}
                        style={{ borderRadius: 8, fontSize: 12 }}
                      >
                        Sync Stats
                      </Button>
                    </div>
                    {/* Summary stat cards */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                      {rows.map((r) => (
                        <div key={r.label} style={{
                          flex: "1 1 80px", textAlign: "center",
                          background: r.bg, borderRadius: 10, padding: "10px 8px",
                        }}>
                          <div style={{ fontWeight: 700, fontSize: 18, color: r.color }}>{r.value}</div>
                          <div style={{ fontSize: 11, color: r.color, opacity: 0.8 }}>{r.label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>
                            {total > 0 ? Math.round((r.value / total) * 100) : 0}%
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Bar chart */}
                    {hasAny ? (
                      <ReactApexChart
                        type="bar"
                        height={220}
                        options={chartOptions}
                        series={[{ name: "Count", data: rows.map((r) => r.value) }]}
                      />
                    ) : (
                      <div style={{ textAlign: "center", padding: "30px 0", color: "#9ca3af", fontSize: 13 }}>
                        No tracking data yet — click "Sync Stats" or wait for emails to be delivered.
                      </div>
                    )}
                  </div>
                );
              })(),
            },
            {
              key: "recipients",
              label: `Recipients (${detail.recipientCount})`,
              children: (() => {
                const allRecipients = detail?.recipients || [];
                const slugName = detail.subject?.replace(/[^a-z0-9]/gi, "_").slice(0, 40);

                // Derive accurate counts from per-recipient event data
                const deliveredRecipients    = allRecipients.filter((r) => r.events?.Delivery && !r.events?.Bounce);
                const bouncedRecipients      = allRecipients.filter((r) => r.events?.Bounce);
                const openedRecipients       = allRecipients.filter((r) => r.events?.Open);
                const undeliveredRecipients  = allRecipients.filter(
                  (r) => !r.events?.Delivery && !r.events?.Bounce && !r.events?.Open && !r.events?.Click
                );

                // Client-side filter
                const displayRecipients =
                  recipientFilter === "delivered"   ? deliveredRecipients   :
                  recipientFilter === "bounced"     ? bouncedRecipients     :
                  recipientFilter === "opened"      ? openedRecipients      :
                  recipientFilter === "undelivered" ? undeliveredRecipients :
                  allRecipients;

                return (
                  <>
                    {/* Filter + download bar */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <Select
                        size="small"
                        value={recipientFilter}
                        onChange={setRecipientFilter}
                        style={{ width: 160 }}
                        options={[
                          { value: "all",          label: `All (${allRecipients.length})` },
                          { value: "delivered",    label: `Delivered (${deliveredRecipients.length})` },
                          { value: "opened",       label: `Opened (${openedRecipients.length})` },
                          { value: "bounced",      label: `Bounced (${bouncedRecipients.length})` },
                          { value: "undelivered",  label: `No Status (${undeliveredRecipients.length})` },
                        ]}
                      />
                      <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {deliveredRecipients.length > 0 && (
                          <Button size="small" icon={<DownloadOutlined />}
                            style={{ borderRadius: 8, borderColor: "#16a34a", color: "#16a34a" }}
                            onClick={() => downloadFilteredRecipients(log._id, "delivered", `delivered_${slugName}_${dayjs().format("YYYYMMDD")}.xlsx`)}>
                            Download Delivered ({deliveredRecipients.length})
                          </Button>
                        )}
                        {openedRecipients.length > 0 && (
                          <Button size="small" icon={<DownloadOutlined />}
                            style={{ borderRadius: 8, borderColor: "#1d4ed8", color: "#1d4ed8" }}
                            onClick={() => downloadFilteredRecipients(log._id, "opened", `opened_${slugName}_${dayjs().format("YYYYMMDD")}.xlsx`)}>
                            Download Opened ({openedRecipients.length})
                          </Button>
                        )}
                        {bouncedRecipients.length > 0 && (
                          <Button size="small" icon={<DownloadOutlined />}
                            style={{ borderRadius: 8, borderColor: "#f59e0b", color: "#f59e0b" }}
                            onClick={() => downloadFilteredRecipients(log._id, "bounced", `bounced_${slugName}_${dayjs().format("YYYYMMDD")}.xlsx`)}>
                            Download Bounced ({bouncedRecipients.length})
                          </Button>
                        )}
                        {undeliveredRecipients.length > 0 && (
                          <Button size="small" icon={<DownloadOutlined />}
                            style={{ borderRadius: 8, borderColor: "#6b7280", color: "#6b7280" }}
                            onClick={() => downloadFilteredRecipients(log._id, "undelivered", `no_status_${slugName}_${dayjs().format("YYYYMMDD")}.xlsx`)}>
                            Download No Status ({undeliveredRecipients.length})
                          </Button>
                        )}
                        {allRecipients.length > 0 && (
                          <Button size="small" icon={<DownloadOutlined />}
                            style={{ borderRadius: 8 }}
                            onClick={() => downloadFilteredRecipients(log._id, "all", `all_recipients_${slugName}_${dayjs().format("YYYYMMDD")}.xlsx`)}>
                            Download All ({allRecipients.length})
                          </Button>
                        )}
                      </div>
                    </div>

                    <Table
                      dataSource={displayRecipients}
                      columns={recipientCols}
                      rowKey={(r) => r.email}
                      size="small"
                      scroll={{ x: "max-content", y: 320 }}
                      pagination={{
                        size:            "small",
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        defaultPageSize: 10,
                      }}
                      locale={{ emptyText: "No recipients found" }}
                    />
                  </>
                );
              })(),
            },
          ]}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
          Failed to load email detail.
        </div>
      )}
    </Modal>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AdminEmailNotification() {
  const [form]              = Form.useForm();
  const [senderForm]        = Form.useForm();
  const [resendForm]        = Form.useForm();
  const [target, setTarget] = useState("excel");
  const [companies, setCompanies]               = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [submitting, setSubmitting]             = useState(false);
  const [result, setResult]                     = useState(null);
  const formTopRef = useRef(null); // scroll anchor

  // ── Scheduler state ──────────────────────────────────────────────────────────
  const [scheduleMode, setScheduleMode] = useState("immediate"); // "immediate" | "scheduled"
  const [scheduleAt,   setScheduleAt]   = useState(null);        // dayjs object

  // ── Sender management state ────────────────────────────────────────────────
  const [senders, setSenders]               = useState([]);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [addSenderModal, setAddSenderModal] = useState(false);
  const [addingSender, setAddingSender]     = useState(false);

  const loadSenders = useCallback(async () => {
    setSendersLoading(true);
    try {
      const res = await api.get("/notifications/email-senders");
      const list = res.data?.data || [];
      setSenders(list);
      // Pre-select the default sender
      const def = list.find((s) => s.isDefault);
      if (def) setSelectedSenderId(def._id);
    } catch (_) {}
    finally { setSendersLoading(false); }
  }, []);

  useEffect(() => { loadSenders(); }, [loadSenders]);

  const handleAddSender = async () => {
    try {
      const vals = await senderForm.validateFields();
      setAddingSender(true);
      await api.post("/notifications/email-senders", vals);
      senderForm.resetFields();
      setAddSenderModal(false);
      await loadSenders();
    } catch (err) {
      if (err?.response?.data?.message) {
        senderForm.setFields([{ name: "email", errors: [err.response.data.message] }]);
      }
    } finally { setAddingSender(false); }
  };

  const handleSetDefault = async (id) => {
    await api.put(`/notifications/email-senders/${id}/default`);
    await loadSenders();
  };

  const handleDeleteSender = async (id) => {
    await api.delete(`/notifications/email-senders/${id}`);
    await loadSenders();
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Sent history
  const [logs, setLogs]               = useState([]);
  const [logsTotal, setLogsTotal]     = useState(0);
  const [logsPage, setLogsPage]       = useState(1);
  const [logsLimit, setLogsLimit]     = useState(10);
  const [logsLoading, setLogsLoading] = useState(false);
  const [viewLog, setViewLog]         = useState(null);
  const [viewBatch, setViewBatch]     = useState(null); // batch job preview
  const [selectedRowKeys, setSelectedRowKeys]           = useState([]);
  const [selectedRowsMap, setSelectedRowsMap]           = useState({}); // persists across pages
  const [bulkDownloading, setBulkDownloading]           = useState(false);
  const [bulkResendModal, setBulkResendModal]           = useState(false);
  const [bulkResending,   setBulkResending]             = useState(false);
  const [bulkResendResults, setBulkResendResults]       = useState([]);
  const [bulkSenderMode,  setBulkSenderMode]            = useState("original");
  const [bulkResendSenderId, setBulkResendSenderId]     = useState(null);
  const [bulkResendFilter, setBulkResendFilter]         = useState("opened"); // "opened" | "delivered"
  const [bulkResendTemplateLoading, setBulkResendTemplateLoading] = useState(false);
  const [resendScheduleMode, setResendScheduleMode]     = useState("immediate");
  const [resendScheduleAt,   setResendScheduleAt]       = useState(null);
  const [resendSuppMap,      setResendSuppMap]           = useState({});
  const [resendDynamicFields, setResendDynamicFields]   = useState([]);
  const [resendSuppParsing,  setResendSuppParsing]       = useState(false);
  const [resendSuppResult,   setResendSuppResult]        = useState(null);

  // ── Batch config state ────────────────────────────────────────────────────────
  const [batchConfig, setBatchConfig]       = useState(null);
  const [batchConfigLoading, setBatchConfigLoading] = useState(false);
  const [batchConfigSaving,  setBatchConfigSaving]  = useState(false);
  const [batchJobs, setBatchJobs]           = useState([]);
  const [batchJobsLoading, setBatchJobsLoading] = useState(false);
  const [sendCaps, setSendCaps]             = useState({ hourlyUsed: 0, dailyUsed: 0 });

  const loadBatchConfig = useCallback(async () => {
    setBatchConfigLoading(true);
    try {
      const [cfgRes, capsRes] = await Promise.all([
        api.get("/notifications/batch-config"),
        api.get("/notifications/send-caps"),
      ]);
      setBatchConfig(cfgRes.data?.data || null);
      setSendCaps({ dailyUsed: capsRes.data?.dailyUsed ?? 0 });
    } catch (_) {}
    finally { setBatchConfigLoading(false); }
  }, []);

  const loadBatchJobs = useCallback(async () => {
    setBatchJobsLoading(true);
    try {
      // Only fetch pending/in_progress — completed jobs already have EmailLog entries
      // and would show up twice in the history table
      const res = await api.get("/notifications/batch-jobs?status=active&limit=50");
      setBatchJobs(res.data?.data || []);
    } catch (_) {}
    finally { setBatchJobsLoading(false); }
  }, []);

  useEffect(() => { loadBatchConfig(); loadBatchJobs(); }, [loadBatchConfig, loadBatchJobs]);

  const handleSaveBatchConfig = async (field, value) => {
    setBatchConfigSaving(true);
    try {
      const res = await api.put("/notifications/batch-config", { [field]: value });
      setBatchConfig(res.data?.data || batchConfig);
    } catch (_) {}
    finally { setBatchConfigSaving(false); }
  };

  const handleCancelJob = async (jobId) => {
    await api.delete(`/notifications/batch-jobs/${jobId}/cancel`);
    loadBatchJobs();
  };

  const [repairingJobs, setRepairingJobs] = useState(false);
  const handleRepairStuckJobs = async () => {
    setRepairingJobs(true);
    try {
      const res = await api.post("/notifications/repair-stuck-jobs");
      const { repaired, total } = res.data;
      if (repaired > 0) {
        message.success(`Repaired ${repaired}/${total} stuck job(s). Refreshing…`);
        setTimeout(() => { loadBatchJobs(); loadLogs(logsPage); }, 1500);
      } else {
        message.info(res.data.message || "No stuck jobs found");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Repair failed");
    } finally {
      setRepairingJobs(false);
    }
  };

  const [triggeringJob, setTriggeringJob] = useState(null);
  const handleTriggerJob = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      const res = await api.post(`/notifications/batch-jobs/${jobId}/trigger`);
      const msg = res.data?.message || "Batches triggered successfully";
      message.success(msg);
      // Poll until job status changes (up to 30s)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        await loadBatchJobs();
        if (attempts >= 6) { clearInterval(poll); setTriggeringJob(null); }
      }, 5000);
    } catch (err) {
      // FIX 8A: show the actual error from the API instead of swallowing it
      const errMsg = err.response?.data?.message || "Failed to trigger batches";
      message.error(errMsg);
      setTriggeringJob(null);
    }
  };

  // ── AI generation state ───────────────────────────────────────────────────────
  const [aiPrompt, setAiPrompt]             = useState("");
  const [aiGenerating, setAiGenerating]     = useState(false);
  const [aiSuggestions, setAiSuggestions]   = useState([]);
  const [aiError, setAiError]               = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const handleGenerateEmail = async () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    setAiSuggestions([]);
    setAiError(null);
    setSelectedSuggestion(null);
    try {
      const res = await api.post("/ai/generate-content", { prompt: aiPrompt, type: "email" });
      setAiSuggestions(res.data?.suggestions || []);
    } catch (err) {
      setAiError(err.response?.data?.message || "AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    // Fill all form fields at once
    form.setFieldsValue({
      subject: suggestion.subject || "",
      title:   suggestion.title   || "",
      body:    suggestion.body    || "",
    });
  };

  // ── Attachment state ──────────────────────────────────────────────────────────
  const [attachmentFiles, setAttachmentFiles] = useState([]); // array of File objects

  // ── Excel upload state ────────────────────────────────────────────────────────
  const [excelValidEmails,    setExcelValidEmails]    = useState([]);
  const [excelInvalidEmails,  setExcelInvalidEmails]  = useState([]);
  const [excelDuplicateEmails,setExcelDuplicateEmails]= useState([]); // [{ email, count }]
  const [excelRecipients,     setExcelRecipients]     = useState([]); // full row objects
  const [excelColumns,       setExcelColumns]       = useState([]); // detected column headers
  const [excelFileName,      setExcelFileName]      = useState("");
  const [excelParsing,       setExcelParsing]       = useState(false);
  const [excelSending,       setExcelSending]       = useState(false);
  const [excelResult,        setExcelResult]        = useState(null);

  // ── Manual email input state ──────────────────────────────────────────────────
  const [manualEmailInput,  setManualEmailInput]   = useState("");

  // ── Email category state ──────────────────────────────────────────────────────
  const [emailCategories,    setEmailCategories]    = useState({ company: [], normal: [], suspicious: [] });
  const [categoryRecipients, setCategoryRecipients] = useState({ company: [], normal: [], suspicious: [] });
  const [selectedCategories, setSelectedCategories] = useState({ company: false, normal: true, suspicious: false });


  // ── Email preview modal ───────────────────────────────────────────────────────
  const [previewOpen,    setPreviewOpen]    = useState(false);

  // ── Email categorisation helpers ──────────────────────────────────────────────
  const FREE_DOMAINS = new Set([
    "gmail.com","googlemail.com","yahoo.com","yahoo.co.uk","yahoo.fr","yahoo.de",
    "yahoo.in","yahoo.com.au","yahoo.co.in","hotmail.com","hotmail.co.uk","hotmail.fr",
    "outlook.com","outlook.in","live.com","live.in","icloud.com","me.com","mac.com",
    "aol.com","protonmail.com","proton.me","tutanota.com","zoho.com","yandex.com",
    "yandex.ru","mail.com","mail.ru","gmx.com","gmx.net","msn.com","rediffmail.com",
    "rocketmail.com","inbox.com","fastmail.com","hey.com","pm.me",
  ]);
  const SUSPICIOUS_TLDS = new Set([
    "biz","xyz","tk","ml","ga","cf","gq","top","click","loan","win","racing",
    "review","date","download","accountant","science","trade","webcam","men",
    "faith","party","stream","gdn","pw","monster","cyou","bar","cfd","hair",
    "quest","lol","bond","zip","mov",
  ]);
  const DISPOSABLE_DOMAINS = new Set([
    "mailinator.com","guerrillamail.com","tempmail.com","10minutemail.com",
    "throwaway.email","fakeinbox.com","sharklasers.com","trashmail.com",
    "yopmail.com","dispostable.com","mailnull.com","spamgourmet.com",
    "maildrop.cc","mintemail.com","tempr.email","discard.email","spam4.me",
  ]);

  const categorizeEmail = (email) => {
    const lower = email.toLowerCase();
    const atIdx = lower.indexOf("@");
    if (atIdx === -1) return "suspicious";
    const domain     = lower.slice(atIdx + 1);
    const domainParts = domain.split(".");
    const tld        = domainParts[domainParts.length - 1];
    const domainName = domainParts[0];
    if (DISPOSABLE_DOMAINS.has(domain))                        return "suspicious";
    if (SUSPICIOUS_TLDS.has(tld))                              return "suspicious";
    // Domain name mixes letters and digits and is short (e.g. xv12, abc1, r4nd0m)
    if (/\d/.test(domainName) && /[a-z]/i.test(domainName) && domainName.length <= 8) return "suspicious";
    if (FREE_DOMAINS.has(domain))                              return "normal";
    return "company";
  };


  // Preview renders the body exactly as the recipient will see it — no wrapper.
  const buildPreviewHtml = (_subject, _title, body) =>
    body || "<em style='color:#aaa;font-family:Arial,sans-serif;'>No body content yet</em>";

  // ── Dynamic fields state ──────────────────────────────────────────────────────
  const [dynamicFields,  setDynamicFields]  = useState([]);
  const [newFieldInput,  setNewFieldInput]  = useState("");
  // Ref for the body textarea — used to insert placeholders at cursor position
  const bodyTextareaRef = useRef(null);

  const addDynamicField = () => {
    const name = newFieldInput.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
    if (!name) return;
    if (dynamicFields.includes(name)) {
      setNewFieldInput("");
      return;
    }
    setDynamicFields((prev) => [...prev, name]);
    setNewFieldInput("");
  };

  const removeDynamicField = (name) => {
    setDynamicFields((prev) => prev.filter((f) => f !== name));
  };

  /** Insert {{fieldName}} at the cursor position in the body textarea */
  const insertFieldAtCursor = (fieldName) => {
    const placeholder = `{{${fieldName}}}`;
    // Try to get the underlying DOM <textarea> from Ant Design's TextArea ref
    const textarea = bodyTextareaRef.current?.resizableTextArea?.textArea;
    const current  = form.getFieldValue("body") || "";
    if (textarea) {
      const start  = textarea.selectionStart || 0;
      const end    = textarea.selectionEnd   || 0;
      const newVal = current.slice(0, start) + placeholder + current.slice(end);
      form.setFieldsValue({ body: newVal });
      // Restore cursor position after the inserted placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 10);
    } else {
      // Fallback: append at end
      form.setFieldsValue({ body: current + placeholder });
    }
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const parseExcelFile = (file) => {
    setExcelParsing(true);
    setExcelValidEmails([]);
    setExcelInvalidEmails([]);
    setExcelDuplicateEmails([]);
    setExcelRecipients([]);
    setExcelColumns([]);
    setExcelResult(null);
    setExcelFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb   = XLSX.read(data, { type: "array" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

        if (rows.length < 1) {
          setExcelResult({ type: "error", message: "File is empty." });
          setExcelParsing(false);
          return;
        }

        // First row = column headers (lowercased)
        const headers = rows[0].map((h) => String(h ?? "").trim().toLowerCase());
        const emailColIdx = headers.findIndex((h) =>
          h === "email" || h === "email address" || h === "e-mail" ||
          h === "emailaddress" || h === "email_address" || /\bemail\b/.test(h)
        );

        if (emailColIdx === -1) {
          // Fallback: auto-detect which column has the most email-like values
          const colEmailCounts = {};
          rows.slice(1).forEach((row) => {
            (Array.isArray(row) ? row : []).forEach((cell, colIdx) => {
              if (EMAIL_REGEX.test(String(cell ?? "").trim())) {
                colEmailCounts[colIdx] = (colEmailCounts[colIdx] || 0) + 1;
              }
            });
          });
          const detectedColIdx = Object.entries(colEmailCounts)
            .sort(([, a], [, b]) => b - a)[0]?.[0];

          // Collect emails only from the detected email column (or all cells if no column found)
          const allEmails = [];
          rows.slice(1).forEach((row) => {
            if (detectedColIdx !== undefined) {
              const val = String(row[detectedColIdx] ?? "").trim();
              if (val) allEmails.push(val);
            } else {
              (Array.isArray(row) ? row : []).forEach((cell) => {
                const val = String(cell ?? "").trim();
                if (val) allEmails.push(val);
              });
            }
          });

          // Count occurrences in the email column only
          const fallbackCounts = {};
          allEmails.forEach((v) => {
            if (EMAIL_REGEX.test(v)) {
              const lower = v.toLowerCase();
              fallbackCounts[lower] = (fallbackCounts[lower] || 0) + 1;
            }
          });
          const fallbackDuplicates = Object.entries(fallbackCounts)
            .filter(([, c]) => c > 1)
            .sort(([, a], [, b]) => b - a)
            .map(([email, count]) => ({ email, count }));
          const valid   = [...new Set(allEmails.filter((v) => EMAIL_REGEX.test(v)))];
          // Only flag values containing "@" as invalid — bare names/phones are not attempted emails
          const invalid = [...new Set(allEmails.filter((v) => v.includes("@") && !EMAIL_REGEX.test(v) && v.length > 0))];
          const cats   = { company: [], normal: [], suspicious: [] };
          const catRec = { company: [], normal: [], suspicious: [] };
          valid.forEach((email) => {
            const cat = categorizeEmail(email);
            cats[cat].push(email);
            catRec[cat].push({ email });
          });
          setExcelValidEmails(valid);
          setExcelInvalidEmails(invalid);
          setExcelDuplicateEmails(fallbackDuplicates);
          setExcelRecipients(valid.map((email) => ({ email })));
          setEmailCategories(cats);
          setCategoryRecipients(catRec);
          setExcelResult({
            type: "warning",
            message: 'No "email" column header found — scanned all cells for email addresses. Add an "email" header row to enable dynamic fields.',
          });
          return;
        }

        // Parse by header row — each data row becomes a recipient object
        const nonEmptyHeaders = headers.filter(Boolean);
        setExcelColumns(nonEmptyHeaders);

        // Count occurrences across all data rows to detect duplicates
        const headerCounts = {};
        rows.slice(1).forEach((row) => {
          const val = String(row[emailColIdx] ?? "").trim().toLowerCase();
          if (val) headerCounts[val] = (headerCounts[val] || 0) + 1;
        });
        const headerDuplicates = Object.entries(headerCounts)
          .filter(([, c]) => c > 1)
          .sort(([, a], [, b]) => b - a)
          .map(([email, count]) => ({ email, count }));

        const valid      = [];
        const invalid    = [];
        const recipients = [];
        const seen       = new Set();

        rows.slice(1).forEach((row) => {
          const emailVal = String(row[emailColIdx] ?? "").trim();
          const lowerEmail = emailVal.toLowerCase();
          if (!emailVal || seen.has(lowerEmail)) return;
          seen.add(lowerEmail);

          // Build a data object keyed by column header
          const rowData = {};
          headers.forEach((header, i) => {
            if (header) rowData[header] = String(row[i] ?? "").trim();
          });

          if (EMAIL_REGEX.test(emailVal)) {
            valid.push(emailVal);
            recipients.push(rowData);
          } else {
            invalid.push(emailVal);
          }
        });

        // Categorise valid emails into company / normal / suspicious
        const cats  = { company: [], normal: [], suspicious: [] };
        const catRec = { company: [], normal: [], suspicious: [] };
        valid.forEach((email, idx) => {
          const cat = categorizeEmail(email);
          cats[cat].push(email);
          catRec[cat].push(recipients[idx]);
        });

        setExcelValidEmails(valid);
        setExcelInvalidEmails(invalid);
        setExcelDuplicateEmails(headerDuplicates);
        setExcelRecipients(recipients);
        setEmailCategories(cats);
        setCategoryRecipients(catRec);
      } catch (err) {
        setExcelResult({ type: "error", message: "Failed to parse file. Please upload a valid Excel or CSV." });
      } finally {
        setExcelParsing(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent antd Upload from doing its own upload
  };

  // ── Parse comma/newline-separated manual email input ─────────────────────────
  const parseManualEmails = () => {
    const raw = manualEmailInput
      .split(/[,\n;]+/)              // split on comma, newline, or semicolon
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    const allValid = raw.filter((e) => EMAIL_REGEX.test(e));
    const invalid  = raw.filter((e) => !EMAIL_REGEX.test(e));

    if (!allValid.length) {
      setExcelResult({ type: "error", message: "No valid email addresses found. Check the format and try again." });
      return;
    }

    // Count occurrences to detect duplicates, then dedup
    const manualCounts = {};
    allValid.forEach((e) => { manualCounts[e] = (manualCounts[e] || 0) + 1; });
    const manualDuplicates = Object.entries(manualCounts)
      .filter(([, c]) => c > 1)
      .sort(([, a], [, b]) => b - a)
      .map(([email, count]) => ({ email, count }));
    const valid = [...new Set(allValid)];

    const cats   = { company: [], normal: [], suspicious: [] };
    const catRec = { company: [], normal: [], suspicious: [] };

    valid.forEach((email) => {
      const cat = categorizeEmail(email);
      cats[cat].push(email);
      catRec[cat].push({ email, name: "" });
    });

    setExcelValidEmails(valid);
    setExcelInvalidEmails(invalid);
    setExcelDuplicateEmails(manualDuplicates);
    setExcelRecipients(valid.map((email) => ({ email, name: "" })));
    setExcelColumns(["email"]);
    setExcelFileName("");            // no file — manual input
    setEmailCategories(cats);
    setCategoryRecipients(catRec);
    setSelectedCategories({ company: true, normal: true, suspicious: false });
    setExcelResult(null);
  };

  const downloadEmailList = (emails, filename) => {
    const xlsxFilename = filename.replace(/\.[^.]+$/, "") + ".xlsx";
    const ws  = XLSX.utils.aoa_to_sheet([["Email"], ...emails.map((e) => [e])]);
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Emails");
    XLSX.writeFile(wb, xlsxFilename);
  };

  const downloadSampleFile = () => {
    const baseFields = ["email", "name"];
    const extraFields = dynamicFields.filter((f) => !baseFields.includes(f));
    const headers = [...baseFields, ...extraFields];
    const sampleData = [
      ["john.doe@example.com", "John Doe", ...extraFields.map((f) => `Sample ${f}`)],
      ["jane.smith@company.com", "Jane Smith", ...extraFields.map((f) => `Sample ${f}`)],
      ["info@acmecorp.com", "Acme Corp", ...extraFields.map((f) => `Sample ${f}`)],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recipients");
    XLSX.writeFile(wb, "sample_recipients.xlsx");
  };

  const downloadRecipientList = (recipients, columns, filename) => {
    const xlsxFilename = filename.replace(/\.[^.]+$/, "") + ".xlsx";
    const cols = columns.length > 0 ? columns : ["email"];
    const header = cols.map((c) => c.charAt(0).toUpperCase() + c.slice(1));
    const rows = recipients.map((r) => cols.map((c) => r[c] ?? ""));
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Emails");
    XLSX.writeFile(wb, xlsxFilename);
  };

  const handleExcelSend = async () => {
    // Build the list of recipients from only the ticked categories
    const selectedRecipients = [
      ...(selectedCategories.company   ? categoryRecipients.company   : []),
      ...(selectedCategories.normal    ? categoryRecipients.normal    : []),
      ...(selectedCategories.suspicious ? categoryRecipients.suspicious : []),
    ];
    const selectedEmails = selectedRecipients.map((r) => r.email);

    if (!selectedEmails.length) {
      setExcelResult({ type: "error", message: "Please tick at least one email category to send." });
      return;
    }
    const values = form.getFieldsValue();
    if (!values.subject || !values.body) {
      setExcelResult({ type: "error", message: "Please fill in Subject and Body before sending." });
      return;
    }
    setExcelSending(true);
    setExcelResult(null);
    try {
      const fd = new FormData();
      fd.append("subject",     values.subject);
      fd.append("title",       values.title || "");
      fd.append("body",        values.body);
      fd.append("target",      "excel");
      if (values.replyTo)   fd.append("replyTo",      values.replyTo);
      if (selectedSenderId) fd.append("fromSenderId", selectedSenderId);
      fd.append("recipients", JSON.stringify(selectedRecipients));
      if (dynamicFields.length) fd.append("dynamicFields", JSON.stringify(dynamicFields));
      attachmentFiles.forEach((f) => fd.append("attachments", f));
      if (scheduleMode === "scheduled" && scheduleAt) fd.append("startAt", scheduleAt.toISOString());

      const res = await api.post("/notifications/send-email", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExcelResult({ type: "success", message: res.data?.message || `Email sent to ${selectedEmails.length} recipient${selectedEmails.length !== 1 ? "s" : ""}!` });
      form.resetFields(["subject", "title", "body", "replyTo"]);
      // Clear everything after successful send
      setDynamicFields([]);
      setScheduleMode("immediate");
      setScheduleAt(null);
      setExcelValidEmails([]);
      setExcelInvalidEmails([]);
      setExcelRecipients([]);
      setExcelColumns([]);
      setExcelFileName("");
      setManualEmailInput("");
      setEmailCategories({ company: [], normal: [], suspicious: [] });
      setCategoryRecipients({ company: [], normal: [], suspicious: [] });
      setSelectedCategories({ company: false, normal: true, suspicious: false });
      setAttachmentFiles([]);
      setBulkLoadedRecipients(null);
      setBulkLoadedFields([]);
      setSuppMergeResult(null);
      loadLogs(1);
    } catch (err) {
      setExcelResult({ type: "error", message: err.response?.data?.message || "Failed to send emails." });
    } finally {
      setExcelSending(false);
    }
  };

  // ── Load companies ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (target !== "specific" || companies.length > 0) return;
    setCompaniesLoading(true);
    api.post("/superAdmin/allCompanyAdmin", { limit: 1000 })
      .then((res) => {
        const list = res.data?.data || [];
        setCompanies(Array.isArray(list) ? list : []);
      })
      .catch(() => setCompanies([]))
      .finally(() => setCompaniesLoading(false));
  }, [target]);

  // ── Load sent email logs ────────────────────────────────────────────────────
  const loadLogs = useCallback((page = 1, limit) => {
    setLogsLoading(true);
    // Use the provided limit or fall back to current logsLimit state
    const effectiveLimit = limit ?? logsLimit;
    api.get(`/notifications/email-logs?page=${page}&limit=${effectiveLimit}`)
      .then((r) => {
        setLogs(r.data?.data || []);
        setLogsTotal(r.data?.total || 0);
        setLogsPage(page);
      })
      .catch(() => {})
      .finally(() => setLogsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsLimit]);

  useEffect(() => { loadLogs(1); }, [loadLogs]);


  // ── Bulk selection helpers ───────────────────────────────────────────────────
  const handleBulkDownload = async (filter) => {
    setBulkDownloading(true);
    const logRows = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");
    const allRecipients = [];
    const apiFilter = filter === "undelivered" ? "all" : filter;
    for (const row of logRows) {
      try {
        const res = await api.get(`/notifications/email-logs/${row._id}/recipients`, {
          params: { download: "true", filter: apiFilter },
        });
        let data = res.data?.data || [];
        if (filter === "undelivered") {
          data = data.filter((rec) => !rec.events?.Delivery && !rec.events?.Bounce && !rec.events?.Open && !rec.events?.Click);
        }
        allRecipients.push(...data);
      } catch {}
    }
    let rows;
    if (filter === "undelivered") {
      rows = allRecipients.map((rec) => ({
        Name:  rec.name  || "",
        Email: rec.email || "",
      }));
    } else if (filter === "delivered") {
      rows = allRecipients.map((rec) => ({
        Name:             rec.name  || "",
        Email:            rec.email || "",
        "Delivered Time": rec.events?.Delivery ? dayjs(rec.events.Delivery).format("DD MMM YYYY HH:mm:ss") : "",
      }));
    } else if (filter === "opened") {
      rows = allRecipients.map((rec) => ({
        Name:          rec.name  || "",
        Email:         rec.email || "",
        "Opened Time": rec.events?.Open ? dayjs(rec.events.Open).format("DD MMM YYYY HH:mm:ss") : "",
      }));
    } else if (filter === "bounced") {
      rows = allRecipients.map((rec) => ({
        Name:          rec.name  || "",
        Email:         rec.email || "",
        "Bounce Time": rec.events?.Bounce ? dayjs(rec.events.Bounce).format("DD MMM YYYY HH:mm:ss") : "",
      }));
    } else {
      rows = allRecipients.map((rec) => ({
        Name:             rec.name  || "",
        Email:            rec.email || "",
        "Delivered":      rec.events?.Delivery ? "Yes" : "No",
        "Delivered Time": rec.events?.Delivery ? dayjs(rec.events.Delivery).format("DD MMM YYYY HH:mm:ss") : "",
        "Opened":         rec.events?.Open     ? "Yes" : "No",
        "Opened Time":    rec.events?.Open     ? dayjs(rec.events.Open).format("DD MMM YYYY HH:mm:ss")     : "",
        "Bounced":        rec.events?.Bounce   ? "Yes" : "No",
        "Bounce Time":    rec.events?.Bounce   ? dayjs(rec.events.Bounce).format("DD MMM YYYY HH:mm:ss")   : "",
      }));
    }
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No data found" }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recipients");
    XLSX.writeFile(wb, `bulk_${filter}_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    setBulkDownloading(false);
  };

  const handleBulkExportStats = () => {
    const rows = Object.values(selectedRowsMap).map((r) => {
      const total     = r.recipientCount || 0;
      const delivered = r.stats?.netDeliveries ?? r.stats?.deliveries ?? 0;
      const opens     = r.stats?.opens    || 0;
      const bounces   = r.stats?.bounces  || 0;
      return {
        Subject:        r.subject || "",
        Recipients:     total,
        Delivered:      delivered,
        "Delivered %":  total ? `${Math.round((delivered / total) * 100)}%` : "0%",
        Opens:          opens,
        "Opens %":      total ? `${Math.round((opens    / total) * 100)}%` : "0%",
        Bounces:        bounces,
        "Bounces %":    total ? `${Math.round((bounces  / total) * 100)}%` : "0%",
        "Sent At":      r.createdAt ? dayjs(r.createdAt).format("DD MMM YYYY HH:mm:ss") : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No data" }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stats");
    XLSX.writeFile(wb, `bulk_stats_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  // ── Bulk resend to opened recipients ────────────────────────────────────────
  const handleBulkResendOpened = async () => {
    try {
      const values  = await resendForm.validateFields();
      const filter  = bulkResendFilter; // "opened" | "delivered"
      const noLabel = filter === "opened" ? "No opens — skipped" : "No deliveries — skipped";
      setBulkResending(true);
      setBulkResendResults([]);
      const logRows = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");

      const hasSuppData  = Object.keys(resendSuppMap).length > 0;
      const mergeSupp    = (recs) => hasSuppData
        ? recs.map((r) => { const extra = resendSuppMap[(r.email || "").toLowerCase()]; return extra ? { ...r, ...extra } : r; })
        : recs;
      const schedStart   = resendScheduleMode === "scheduled" && resendScheduleAt
        ? resendScheduleAt.toISOString() : null;

      if (bulkSenderMode === "single") {
        const bulkRes = await api.post("/notifications/email-logs/bulk-recipients", {
          logIds: logRows.map((r) => r._id),
          filter,
        });
        const raw           = bulkRes.data?.recipients || [];
        const allRecipients = mergeSupp(raw);
        if (!allRecipients.length) {
          setBulkResendResults([{ subject: "Combined", status: "skipped", message: `No ${filter} recipients found` }]);
        } else {
          try {
            const fd = new FormData();
            fd.append("subject",    values.subject);
            fd.append("title",      values.title || "");
            fd.append("body",       values.body);
            fd.append("target",     "excel");
            if (bulkResendSenderId) fd.append("fromSenderId", bulkResendSenderId);
            fd.append("recipients", JSON.stringify(allRecipients));
            if (resendDynamicFields.length) fd.append("dynamicFields", JSON.stringify(resendDynamicFields));
            if (schedStart) fd.append("startAt", schedStart);
            const sendRes = await api.post("/notifications/send-email", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            setBulkResendResults([{
              subject:        `${logRows.length} campaign${logRows.length !== 1 ? "s" : ""} combined`,
              sender:         senders.find((s) => s._id === bulkResendSenderId)?.email || "default sender",
              recipientCount: allRecipients.length,
              status:         "success",
              message:        sendRes.data?.message || `Sent to ${allRecipients.length} recipients`,
            }]);
          } catch {
            setBulkResendResults([{ subject: `${logRows.length} campaigns combined`, status: "error", message: "Failed to send" }]);
          }
        }
      } else {
        const results = [];
        for (const row of logRows) {
          const count = filter === "opened"
            ? (row.stats?.opens || 0)
            : (row.stats?.netDeliveries ?? row.stats?.deliveries ?? 0);
          if (!count) {
            results.push({ subject: row.subject, sender: row.fromEmail, recipientCount: 0, status: "skipped", message: noLabel });
            continue;
          }
          try {
            const res        = await api.get(`/notifications/email-logs/${row._id}/recipients`, {
              params: { download: "true", filter },
            });
            const raw        = (res.data?.data || []).map((r) => ({ email: r.email, name: r.name || "" }));
            const recipients = mergeSupp(raw);
            if (!recipients.length) {
              results.push({ subject: row.subject, sender: row.fromEmail, recipientCount: 0, status: "skipped", message: noLabel });
              continue;
            }
            const matchedSender = senders.find((s) => s.email === row.fromEmail);
            const fd = new FormData();
            fd.append("subject",    values.subject);
            fd.append("title",      values.title || "");
            fd.append("body",       values.body);
            fd.append("target",     "excel");
            if (matchedSender) fd.append("fromSenderId", matchedSender._id);
            fd.append("recipients", JSON.stringify(recipients));
            if (resendDynamicFields.length) fd.append("dynamicFields", JSON.stringify(resendDynamicFields));
            if (schedStart) fd.append("startAt", schedStart);
            const sendRes = await api.post("/notifications/send-email", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            results.push({
              subject:        row.subject,
              sender:         row.fromEmail || "default",
              matchedSender:  !!matchedSender,
              recipientCount: recipients.length,
              status:         "success",
              message:        sendRes.data?.message || `Sent to ${recipients.length} recipients`,
            });
          } catch {
            results.push({ subject: row.subject, sender: row.fromEmail, recipientCount: 0, status: "error", message: "Failed to send" });
          }
        }
        setBulkResendResults(results);
      }
    } catch {}
    finally { setBulkResending(false); }
  };

  const handleResendSuppExcel = (file) => {
    setResendSuppParsing(true);
    setResendSuppResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data    = new Uint8Array(e.target.result);
        const wb      = XLSX.read(data, { type: "array" });
        const ws      = wb.Sheets[wb.SheetNames[0]];
        const rows    = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) { setResendSuppResult({ error: "File empty or no data rows" }); return; }
        const headers  = rows[0].map((h) => String(h ?? "").trim().toLowerCase());
        const emailIdx = headers.findIndex((h) => /\bemail\b/.test(h) || h === "e-mail");
        if (emailIdx === -1) { setResendSuppResult({ error: "No 'email' column found" }); return; }
        const map = {};
        for (let i = 1; i < rows.length; i++) {
          const row   = rows[i];
          const email = String(row[emailIdx] ?? "").trim().toLowerCase();
          if (!email) continue;
          const extra = {};
          headers.forEach((h, idx) => { if (idx !== emailIdx && h) extra[h] = String(row[idx] ?? "").trim(); });
          map[email] = extra;
        }
        const newCols = headers.filter((h) => h && h !== headers[emailIdx] && h !== "name");
        setResendSuppMap(map);
        setResendDynamicFields((prev) => Array.from(new Set([...prev, ...newCols])));
        setResendSuppResult({ matched: 0, total: Object.keys(map).length, newColumns: newCols });
      } catch (err) {
        setResendSuppResult({ error: "Failed to parse: " + err.message });
      } finally {
        setResendSuppParsing(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  // ── Use as Template — prefill form from history ────────────────────────────
  const handleUseAsTemplate = (detail) => {
    form.setFieldsValue({
      subject: detail.subject,
      title:   detail.title || "",
      body:    detail.body  || "",
    });
    // Clear dynamic fields so stale fields from a previous "Send to Opened"
    // action don't bleed into an unrelated template
    setDynamicFields([]);
    setBulkLoadedRecipients(null);
    setBulkLoadedFields([]);
    setSuppMergeResult(null);
    setResult(null);
    setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ── Target recipients from history (Target Delivered / Target Opened) ────────
  const handleTargetRecipients = (recipientList, detectedFields = []) => {
    // Categorise by email domain for the existing category UI
    const company = []; const normal = []; const suspicious = [];
    const companyR = []; const normalR = []; const suspiciousR = [];
    recipientList.forEach((r) => {
      const cat = categorizeEmail(r.email);
      if (cat === "company")    { company.push(r.email);    companyR.push(r);    }
      else if (cat === "normal") { normal.push(r.email);     normalR.push(r);     }
      else                       { suspicious.push(r.email); suspiciousR.push(r); }
    });

    // Columns = all detected fields + email + name
    const allFields = Array.from(new Set([
      "email", "name",
      ...detectedFields,
      ...recipientList.flatMap((r) => Object.keys(r.data || {})),
    ]));

    setTarget("excel");
    setExcelRecipients(recipientList);
    setExcelColumns(allFields.length ? allFields : ["email", "name"]);
    setExcelFileName("targeted-recipients");
    setEmailCategories({ company, normal, suspicious });
    setCategoryRecipients({ company: companyR, normal: normalR, suspicious: suspiciousR });
    setSelectedCategories({ company: true, normal: true, suspicious: false });
    // Set ALL dynamic field chips from original campaign — no exclusions.
    // If the original Excel had name, email, company → all 3 chips shown.
    setDynamicFields(detectedFields);
    setResult(null);
    setBulkLoadedRecipients(recipientList);   // track for supplemental merge
    setBulkLoadedFields(detectedFields);
    setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ── Bulk "Send to..." from selected campaigns ─────────────────────────────────
  // State: track that recipients were bulk-loaded (shows supplemental Excel UI)
  const [bulkLoadedRecipients, setBulkLoadedRecipients] = useState(null); // null = not bulk-loaded
  const [bulkLoadedFields,     setBulkLoadedFields]     = useState([]);
  const [bulkTargeting,        setBulkTargeting]        = useState(null);  // "opened"|"delivered"|"all"|null
  const [suppExcelParsing,     setSuppExcelParsing]     = useState(false);
  const [suppMergeResult,      setSuppMergeResult]      = useState(null);  // { matched, unmatched }

  const handleBulkTarget = async (filter) => {
    const logRows = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");
    if (!logRows.length) { message.warning("Select at least one campaign from the history table"); return; }

    setBulkTargeting(filter);
    try {
      const res = await api.post("/notifications/email-logs/bulk-recipients", {
        logIds: logRows.map((r) => r._id),
        filter,
      });
      const { recipients = [], dynamicFields: detected = [] } = res.data;
      if (!recipients.length) {
        message.warning(`No ${filter === "all" ? "" : filter + " "}recipients found in selected campaigns`);
        return;
      }
      message.success(`${recipients.length} unique recipients loaded from ${logRows.length} campaign${logRows.length !== 1 ? "s" : ""}`);
      handleTargetRecipients(recipients, detected);
      setSuppMergeResult(null);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load recipients");
    } finally {
      setBulkTargeting(null);
    }
  };

  // Supplemental Excel merge: match by email, add new columns to existing recipient data
  const handleSuppExcelUpload = (file) => {
    setSuppExcelParsing(true);
    setSuppMergeResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data   = new Uint8Array(e.target.result);
        const wb     = XLSX.read(data, { type: "array" });
        const ws     = wb.Sheets[wb.SheetNames[0]];
        const rows   = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) { setSuppExcelParsing(false); setSuppMergeResult({ error: "File is empty or has no data rows" }); return; }

        const headers = rows[0].map((h) => String(h ?? "").trim().toLowerCase());
        const emailIdx = headers.findIndex((h) => /\bemail\b/.test(h) || h === "e-mail");
        if (emailIdx === -1) { setSuppExcelParsing(false); setSuppMergeResult({ error: "No 'email' column found in supplemental file" }); return; }

        // Build lookup: email (lowercase) → extra data from supplemental file
        const suppMap = {};
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const email = String(row[emailIdx] ?? "").trim().toLowerCase();
          if (!email) continue;
          const extraData = {};
          headers.forEach((h, idx) => {
            if (idx !== emailIdx && h) extraData[h] = String(row[idx] ?? "").trim();
          });
          suppMap[email] = extraData;
        }

        // Merge into current excelRecipients
        let matched = 0; let unmatched = 0;
        const newColumns = headers.filter((h) => h && h !== headers[emailIdx]);

        setExcelRecipients((prev) => prev.map((r) => {
          const key = (r.email || "").toLowerCase();
          const extra = suppMap[key];
          if (extra) { matched++; return { ...r, data: { ...(r.data || {}), ...extra } }; }
          unmatched++;
          return r;
        }));

        // Add new columns to detected dynamic fields
        setExcelColumns((prev) => Array.from(new Set([...prev, ...newColumns])));
        setDynamicFields((prev) => Array.from(new Set([
          ...prev,
          ...newColumns.filter((c) => c !== "email" && c !== "name"),
        ])));

        setSuppMergeResult({ matched, unmatched, newColumns });
      } catch (err) {
        setSuppMergeResult({ error: "Failed to parse file: " + err.message });
      } finally {
        setSuppExcelParsing(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent antd upload
  };

  // ── Send email ──────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setResult(null);
    try {
      // Use FormData so multer can parse text fields + file attachments in one request
      const fd = new FormData();
      fd.append("subject",  values.subject);
      fd.append("title",    values.title || "");
      fd.append("body",     values.body);
      fd.append("target",   target);
      if (values.replyTo)       fd.append("replyTo",      values.replyTo);
      if (selectedSenderId)     fd.append("fromSenderId", selectedSenderId);
      if (target === "specific") {
        (values.targetCompanyIds || []).forEach((id) => fd.append("targetCompanyIds[]", id));
      }
      if (dynamicFields.length) fd.append("dynamicFields", JSON.stringify(dynamicFields));
      attachmentFiles.forEach((f) => fd.append("attachments", f));
      if (scheduleMode === "scheduled" && scheduleAt) fd.append("startAt", scheduleAt.toISOString());

      const res = await api.post("/notifications/send-email", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msg = res.data?.batched
        ? `${res.data.message}. First batch fires at ${dayjs(res.data.firstBatchAt).format("DD MMM YYYY, hh:mm A")} · Last at ${dayjs(res.data.lastBatchAt).format("hh:mm A")}${res.data.warning ? ` · ⚠️ ${res.data.warning}` : ""}`
        : res.data?.message || "Email sent successfully!";
      setResult({ type: "success", message: msg });
      if (res.data?.batched) loadBatchJobs();
      form.resetFields(["subject", "title", "body", "targetCompanyIds", "replyTo"]);
      // Clear attachments, dynamic fields, scheduler after successful send
      setAttachmentFiles([]);
      setDynamicFields([]);
      setScheduleMode("immediate");
      setScheduleAt(null);
      loadLogs(1);
    } catch (err) {
      setResult({ type: "error", message: err.response?.data?.message || "Failed to send email." });
    } finally {
      setSubmitting(false);
    }
  };

  const companyOptions = companies.map((c) => {
    const companyName = c.userInfo?.companyName;
    const ownerName   = [c.firstname, c.lastname].filter(Boolean).join(" ");
    const displayName = companyName
      ? `${companyName}${ownerName ? ` — ${ownerName}` : ""}`
      : ownerName || c.email;
    const label = c.email ? `${displayName} (${c.email})` : displayName;
    return { value: c._id, label };
  });

  // ── Table data ────────────────────────────────────────────────────────────────
  // Batch jobs (active: pending / in_progress) are shown in their OWN non-paginated
  // section above the history table.  Mixing them into the server-paginated logs
  // table broke pagination: Ant Design slices dataSource by (page-1)*pageSize, so
  // the "extra" batch rows offset the slice window and page 2+ showed only 1-2 rows.
  const batchData = batchJobs
    .map((j) => ({ ...j, _rowType: "batch", _rowKey: `batch-${j.jobId}` }))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // History table: email logs only — exactly pageSize rows per page, server-paginated.
  const combinedData = logs.map((l) => ({ ...l, _rowType: "log", _rowKey: `log-${l._id}` }));

  // ── History table columns ────────────────────────────────────────────────────
  const logColumns = [
    {
      title: "Subject",
      key: "subject",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{r.subject}</div>
          {r.title && (
            <div
              style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}
              dangerouslySetInnerHTML={{ __html: r.title.length > 60 ? r.title.slice(0, 60) + "…" : r.title }}
            />
          )}
          {/* Batch progress bar */}
          {r._rowType === "batch" && (
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {r.completedBatches}/{r.totalBatches} batches
                {r.droppedCount > 0 && <span style={{ color: "#ef4444", marginLeft: 4 }}>· {r.droppedCount} dropped</span>}
              </div>
              <div style={{ background: "#e5e7eb", borderRadius: 4, height: 4, marginTop: 3, overflow: "hidden" }}>
                <div style={{
                  width: `${r.totalBatches > 0 ? Math.round((r.completedBatches / r.totalBatches) * 100) : 0}%`,
                  background: r.status === "completed" ? "#22c55e" : r.status === "failed" ? "#ef4444" : "#f59e0b",
                  height: "100%", borderRadius: 4, transition: "width 0.3s",
                }} />
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      key: "type",
      render: (_, r) => {
        if (r._rowType === "batch") {
          return <Tag color="orange" style={{ borderRadius: 20, fontWeight: 500 }}>Batch</Tag>;
        }
        const opt = targetLabel(r.target);
        return (
          <Tag color={opt?.tagColor || "default"} style={{ borderRadius: 20, fontWeight: 500 }}>
            {opt?.label || r.target}
          </Tag>
        );
      },
    },
    {
      title: "Recipients",
      key: "recipientCount",
      align: "center",
      render: (_, r) => (
        <span style={{
          background: "#f3f4f6", borderRadius: 20, padding: "2px 10px",
          fontSize: 12, fontWeight: 600, color: "#374151",
        }}>
          {r._rowType === "batch" ? r.totalRecipients : (r.recipientCount ?? 0)}
        </span>
      ),
    },
    {
      title: "Delivered",
      key: "delivered",
      align: "center",
      render: (_, r) => {
        // Prefer netDeliveries (delivered AND not bounced, per-recipient).
        // Fall back to deliveries for older records that haven't been recomputed yet.
        const val   = r._rowType === "log"
          ? (r.stats?.netDeliveries || r.stats?.deliveries || 0)
          : (r.stats?.deliveries || 0);
        const total = r._rowType === "batch" ? (r.totalRecipients || 0) : (r.recipientCount || 0);
        if (!total) return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
        const pct = Math.round((val / total) * 100);
        return (
          <div style={{ textAlign: "center", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#15803d" }}>{val}</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{pct}%</div>
          </div>
        );
      },
    },
    {
      title: "Opens",
      key: "opens",
      align: "center",
      render: (_, r) => {
        const val   = r.stats?.opens || 0;
        const total = r._rowType === "batch" ? (r.totalRecipients || 0) : (r.recipientCount || 0);
        if (!total) return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
        const pct = Math.round((val / total) * 100);
        return (
          <div style={{ textAlign: "center", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1d4ed8" }}>{val}</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{pct}%</div>
          </div>
        );
      },
    },
    {
      title: "Bounces",
      key: "bounces",
      align: "center",
      render: (_, r) => {
        const val   = r.stats?.bounces || 0;
        const total = r._rowType === "batch" ? (r.totalRecipients || 0) : (r.recipientCount || 0);
        if (!total) return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
        const pct = Math.round((val / total) * 100);
        return (
          <div style={{ textAlign: "center", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: val > 0 ? "#dc2626" : "#9ca3af" }}>{val}</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{pct}%</div>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "batchStatus",
      render: (_, r) => {
        if (r._rowType === "batch") {
          const color = r.status === "completed" ? "green" : r.status === "in_progress" ? "blue" : r.status === "failed" ? "red" : r.status === "cancelled" ? "default" : "orange";
          // Show when the first batch is scheduled
          const firstBatch   = (r.batches || [])[0];
          const scheduleInfo = (() => {
            // New jobs: scheduledStartAt explicitly records the user's original intent
            if (r.scheduledStartAt) return dayjs(r.scheduledStartAt).format("DD MMM YYYY, hh:mm A");
            if (r.scheduledStartAt === null) return "Immediately";
            // Legacy jobs (field not present): fall back to first-batch heuristic
            if (!firstBatch?.scheduledAt) return "Immediately";
            const diffMs = new Date(firstBatch.scheduledAt) - new Date(r.createdAt);
            return diffMs <= 2 * 60_000 ? "Immediately" : dayjs(firstBatch.scheduledAt).format("DD MMM YYYY, hh:mm A");
          })();
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Tag color={color} style={{ margin: 0 }}>{r.status.toUpperCase()}</Tag>
              {scheduleInfo && (
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  <ClockCircleOutlined style={{ marginRight: 3 }} />{scheduleInfo}
                </span>
              )}
              {["pending", "in_progress"].includes(r.status) && (
                <div style={{ display: "flex", gap: 4 }}>
                  {/* ⚡ Force-send all pending batches immediately, bypassing EventBridge */}
                  <Tooltip title="Force send now — processes all pending batches immediately on this server, bypassing EventBridge scheduler">
                    <Popconfirm
                      title="Force send pending batches?"
                      description="This will immediately send all pending batches without waiting for the scheduled time. Use this if the job is stuck or for testing."
                      onConfirm={() => handleTriggerJob(r.jobId)}
                      okText="Yes, send now"
                      okButtonProps={{ style: { background: "#f59e0b", borderColor: "#f59e0b" } }}
                      cancelText="Cancel"
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={triggeringJob === r.jobId}
                        style={{ background: "#f59e0b", borderColor: "#f59e0b" }}
                      />
                    </Popconfirm>
                  </Tooltip>
                  <Popconfirm title="Cancel this batch job?" onConfirm={() => handleCancelJob(r.jobId)} okText="Cancel Job" okButtonProps={{ danger: true }}>
                    <Button size="small" danger icon={<StopOutlined />} />
                  </Popconfirm>
                </div>
              )}
            </div>
          );
        }
        // Regular email log — always "Sent"
        return <Tag color="green" style={{ margin: 0 }}>SENT</Tag>;
      },
    },
    {
      title: "Sent At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (
        <Tooltip title={dayjs(v).format("DD MMM YYYY HH:mm")}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {dayjs(v).fromNow()}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, r) => {
        if (r._rowType === "batch") {
          return (
            <Tooltip title="Preview email content">
              <Button
                size="small"
                icon={<EyeOutlined />}
                style={{ borderRadius: 8 }}
                onClick={() => setViewBatch(r)}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="View / Use as Template">
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={{ borderRadius: 8 }}
              onClick={() => setViewLog(r)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content" style={{ margin: "0 auto" }}>

        {/* Scroll anchor */}
        <div ref={formTopRef} />

        {/* Page header */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <MailOutlined style={{ fontSize: 24, color: "#6366f1" }} />
          <div>
            <h4 className="mb-0" style={{ fontWeight: 700 }}>Email Notification</h4>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>Send broadcast emails to your users</p>
          </div>
        </div>

        {/* ── Sender Address Management ──────────────────────────────────────── */}
        <Card
          style={{ borderRadius: 12, marginBottom: 28 }}
          styles={{ body: { padding: 20 } }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MailOutlined style={{ color: "#6366f1", fontSize: 18 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Sender Email Addresses</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Manage which "From" addresses can be used when sending emails</div>
              </div>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              style={{ background: "#6366f1", borderColor: "#6366f1" }}
              onClick={() => setAddSenderModal(true)}
            >
              Add Email
            </Button>
          </div>

          {sendersLoading ? (
            <Spin size="small" />
          ) : senders.length === 0 ? (
            <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 0" }}>
              No sender addresses configured — emails will use the default <code>noreply@voycell.com</code>.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {senders.map((s) => (
                <div
                  key={s._id}
                  onClick={() => setSelectedSenderId(s._id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 14px", borderRadius: 10, cursor: "pointer",
                    border: `2px solid ${selectedSenderId === s._id ? "#6366f1" : "#e5e7eb"}`,
                    background: selectedSenderId === s._id ? "#ede9fe" : "#fff",
                    transition: "all 0.2s",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{s.email}</div>
                  </div>
                  {s.isDefault && (
                    <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>Default</Tag>
                  )}
                  <div style={{ display: "flex", gap: 4, marginLeft: 4 }}>
                    {!s.isDefault && (
                      <Tooltip title="Set as default">
                        <Button
                          size="small" type="text"
                          icon={<StarOutlined style={{ color: "#f59e0b" }} />}
                          onClick={(e) => { e.stopPropagation(); handleSetDefault(s._id); }}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Remove">
                      <Button
                        size="small" type="text" danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => { e.stopPropagation(); handleDeleteSender(s._id); }}
                      />
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedSenderId && (() => {
            const sel = senders.find((s) => s._id === selectedSenderId);
            return sel ? (
              <div style={{ marginTop: 12, fontSize: 12, color: "#6366f1", fontWeight: 500 }}>
                <MailOutlined style={{ marginRight: 4 }} />
                Emails will be sent from: <strong>{sel.name} &lt;{sel.email}&gt;</strong>
              </div>
            ) : null;
          })()}
        </Card>

        {/* ── Add Sender Modal ─────────────────────────────────────────────────── */}
        <Modal
          title={<span><PlusOutlined style={{ marginRight: 8, color: "#6366f1" }} />Add Sender Email</span>}
          open={addSenderModal}
          onCancel={() => { setAddSenderModal(false); senderForm.resetFields(); }}
          onOk={handleAddSender}
          okText="Add"
          confirmLoading={addingSender}
          okButtonProps={{ style: { background: "#6366f1", borderColor: "#6366f1" } }}
          width={420}
        >
          <Form form={senderForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input placeholder="hello@voycell.com" />
            </Form.Item>
            <Form.Item
              name="name"
              label="Display Name"
              extra="Shown in the recipient's inbox as the sender name"
            >
              <Input placeholder="VOYCELL Support" />
            </Form.Item>
            <Form.Item name="isDefault" valuePropName="checked" initialValue={false}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" onChange={(e) => senderForm.setFieldValue("isDefault", e.target.checked)} />
                <span style={{ fontSize: 13 }}>Set as default sender</span>
              </label>
            </Form.Item>
          </Form>
        </Modal>

        {/* ── Batch Sending Configuration ──────────────────────────────────── */}
        <Card style={{ borderRadius: 12, marginBottom: 28 }} styles={{ body: { padding: 20 } }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ClockCircleOutlined style={{ color: "#f59e0b", fontSize: 18 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Batch Sending</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Control how many emails are sent and at what interval</div>
              </div>
            </div>
            {batchConfig && (
              <Switch
                checked={batchConfig.enabled}
                loading={batchConfigSaving}
                checkedChildren="Batched"
                unCheckedChildren="Instant"
                onChange={(v) => handleSaveBatchConfig("enabled", v)}
              />
            )}
          </div>

          {batchConfigLoading ? <Spin size="small" /> : batchConfig && (
            <>
              {/* Daily usage */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>DAILY USAGE</div>
                  <Progress
                    percent={Math.min(100, Math.round((sendCaps.dailyUsed / batchConfig.dailyCap) * 100))}
                    size="small"
                    strokeColor={sendCaps.dailyUsed >= batchConfig.dailyCap ? "#ef4444" : "#6366f1"}
                  />
                  <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>
                    {sendCaps.dailyUsed} / {batchConfig.dailyCap} today
                  </div>
                </div>
              </div>

              {/* Config controls */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, opacity: batchConfig.enabled ? 1 : 0.45, pointerEvents: batchConfig.enabled ? "auto" : "none" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Batch Size</div>
                  <InputNumber
                    min={1} max={500} value={batchConfig.batchSize}
                    style={{ width: 120 }}
                    addonAfter="emails"
                    onBlur={(e) => handleSaveBatchConfig("batchSize", Number(e.target.value))}
                  />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>emails per batch</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                    Interval between batches
                    {batchConfig.intervalUnit === "seconds" && batchConfig.intervalValue < 60 && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#f59e0b" }}>⚡ direct mode</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <InputNumber
                      min={1}
                      value={batchConfig.intervalValue}
                      style={{ width: 90 }}
                      onBlur={(e) => handleSaveBatchConfig("intervalValue", Number(e.target.value))}
                    />
                    <Select
                      value={batchConfig.intervalUnit}
                      style={{ width: 110 }}
                      onChange={(v) => handleSaveBatchConfig("intervalUnit", v)}
                      options={[
                        { value: "seconds", label: "Seconds" },
                        { value: "minutes", label: "Minutes" },
                        { value: "hours",   label: "Hours"   },
                        { value: "days",    label: "Days"    },
                      ]}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                    {batchConfig.intervalUnit === "seconds" && batchConfig.intervalValue < 60
                      ? "< 60s: batches sent directly (no EventBridge)"
                      : "≥ 60s: schedule via API"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Daily Cap</div>
                  <InputNumber
                    min={1} value={batchConfig.dailyCap}
                    style={{ width: 130 }}
                    addonAfter="/day"
                    onBlur={(e) => handleSaveBatchConfig("dailyCap", Number(e.target.value))}
                  />
                </div>
              </div>

              {batchConfig.enabled && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#f59e0b", fontWeight: 500 }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  Batched mode ON — {batchConfig.batchSize} emails every {batchConfig.intervalValue} {batchConfig.intervalUnit}
                  {batchConfig.intervalSeconds >= 60
                    ? ""
                    : " (direct send, sub-60s)"}
                </div>
              )}
            </>
          )}

        </Card>

        {/* ── Audience selector ── */}
        <div className="mb-4">
          <div className="fw-semibold mb-2" style={{ fontSize: 14, color: "#374151" }}>Select Audience</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {TARGET_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                onClick={() => { setTarget(opt.value); setResult(null); form.resetFields(["targetCompanyIds"]); }}
                style={{
                  flex: "1 1 200px",
                  border: `2px solid ${target === opt.value ? opt.border : "#e5e7eb"}`,
                  borderRadius: 12, padding: "14px 16px", cursor: "pointer",
                  background: target === opt.value ? opt.color : "#fff",
                  transition: "all 0.2s",
                  display: "flex", alignItems: "flex-start", gap: 12,
                }}
              >
                <div style={{ marginTop: 2 }}>{opt.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>{opt.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Compose form ── */}
        <Card style={{ borderRadius: 12, marginBottom: 32 }} styles={{ body: { padding: 24 } }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>

            {/* ── Excel upload panel ── */}
            {target === "excel" && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 10 }}>
                  <FileExcelOutlined style={{ color: "#16a34a", marginRight: 6 }} />
                  Add Recipients
                </div>

                {/* ── Manual email input ── */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                    Type or paste email addresses (comma, semicolon, or line-separated):
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Input.TextArea
                      value={manualEmailInput}
                      onChange={(e) => setManualEmailInput(e.target.value)}
                      placeholder="e.g. alice@example.com, bob@company.com"
                      autoSize={{ minRows: 2, maxRows: 5 }}
                      style={{ flex: 1, borderRadius: 8, fontSize: 13 }}
                    />
                    <Button
                      type="primary"
                      onClick={parseManualEmails}
                      disabled={!manualEmailInput.trim()}
                      style={{ borderRadius: 8, background: "#6366f1", borderColor: "#6366f1" }}
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* ── Divider ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>or upload a file</span>
                  <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Upload
                    accept=".xlsx,.xls,.csv"
                    showUploadList={false}
                    beforeUpload={parseExcelFile}
                  >
                    <Button icon={<UploadOutlined />} loading={excelParsing}>
                      {excelFileName ? `Re-upload (${excelFileName})` : "Choose Excel / CSV File"}
                    </Button>
                  </Upload>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadSampleFile}
                    style={{ color: "#6366f1", borderColor: "#c7d2fe" }}
                  >
                    Download Sample File
                  </Button>
                </div>

                {/* Show category cards whether emails came from manual input OR a file */}
                {!excelParsing && excelValidEmails.length > 0 && (() => {
                  const CAT_CONFIG = [
                    { key: "company",    label: "Company Emails",    color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", dot: "#3b82f6" },
                    { key: "normal",     label: "Normal Emails",     color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", dot: "#22c55e" },
                    { key: "suspicious", label: "Suspicious Emails",  color: "#b45309", bg: "#fffbeb", border: "#fde68a", dot: "#f59e0b" },
                  ];
                  const totalSelected = CAT_CONFIG.reduce((sum, c) =>
                    sum + (selectedCategories[c.key] ? emailCategories[c.key].length : 0), 0);

                  return (
                    <div style={{ marginTop: 14 }}>
                      {/* Category cards */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {CAT_CONFIG.map(({ key, label, color, bg, border, dot }) => {
                          const emails = emailCategories[key];
                          if (emails.length === 0) return null;
                          return (
                            <div key={key} style={{
                              background: bg, border: `1px solid ${border}`,
                              borderRadius: 10, padding: "12px 16px",
                              opacity: emails.length === 0 ? 0.5 : 1,
                            }}>
                              {/* Header row: checkbox + label + actions */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedCategories[key]}
                                    onChange={(e) => setSelectedCategories(prev => ({ ...prev, [key]: e.target.checked }))}
                                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: dot }}
                                  />
                                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: dot }} />
                                  <span style={{ fontWeight: 700, fontSize: 14, color }}>{label}</span>
                                  <span style={{ background: color, color: "#fff", borderRadius: 20, padding: "1px 10px", fontSize: 13, fontWeight: 700 }}>{emails.length}</span>
                                </label>
                                <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadRecipientList(categoryRecipients[key], excelColumns, `${key}_emails.xlsx`)}>
                                  Download
                                </Button>
                              </div>

                              <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.8, paddingLeft: 26 }}>
                                {emails.slice(0, 5).join(", ")}
                                {emails.length > 5 && ` … +${emails.length - 5} more`}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Invalid / skipped row */}
                      {excelInvalidEmails.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, padding: "8px 12px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca" }}>
                          <CloseCircleOutlined style={{ color: "#ef4444" }} />
                          <span style={{ fontSize: 13, color: "#991b1b" }}>
                            <strong>{excelInvalidEmails.length}</strong> invalid / skipped
                          </span>
                          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadEmailList(excelInvalidEmails, "invalid_emails.txt")}>
                            Download
                          </Button>
                        </div>
                      )}

                      {/* Duplicate emails row */}
                      {excelDuplicateEmails.length > 0 && (
                        <div style={{ marginTop: 8, padding: "10px 14px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <WarningOutlined style={{ color: "#d97706" }} />
                              <span style={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
                                {excelDuplicateEmails.length} duplicate email{excelDuplicateEmails.length !== 1 ? "s" : ""} found — kept first occurrence only
                              </span>
                            </span>
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => downloadEmailList(excelDuplicateEmails.map(({ email, count }) => `${email} (×${count})`), "duplicate_emails.txt")}
                            >
                              Download
                            </Button>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {excelDuplicateEmails.slice(0, 20).map(({ email, count }) => (
                              <span key={email} style={{
                                fontSize: 12, fontFamily: "monospace",
                                background: "#fef3c7", border: "1px solid #fcd34d",
                                borderRadius: 4, padding: "2px 8px", color: "#78350f",
                              }}>
                                {email} <strong style={{ color: "#b45309" }}>×{count}</strong>
                              </span>
                            ))}
                            {excelDuplicateEmails.length > 20 && (
                              <span style={{ fontSize: 12, color: "#92400e" }}>
                                … +{excelDuplicateEmails.length - 20} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Selected total */}
                      <div style={{ marginTop: 12, padding: "10px 14px", background: "#f8faff", borderRadius: 8, border: "1px solid #e0e7ff", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <span style={{ fontSize: 13, color: "#374151" }}>
                          Selected for sending:&nbsp;
                          <strong style={{ color: "#4f46e5", fontSize: 15 }}>{totalSelected}</strong>
                          &nbsp;email{totalSelected !== 1 ? "s" : ""}
                          {CAT_CONFIG.filter(c => selectedCategories[c.key] && emailCategories[c.key].length > 0).map(c => (
                            <span key={c.key} style={{ marginLeft: 8, fontSize: 11, color: c.color }}>
                              {emailCategories[c.key].length} {c.label.toLowerCase()}
                            </span>
                          ))}
                        </span>
                        {batchConfig?.enabled && totalSelected > 0 && (() => {
                          const batchCount = Math.ceil(totalSelected / batchConfig.batchSize);
                          const gapSeconds = (batchCount - 1) * batchConfig.intervalSeconds;
                          return gapSeconds > 0 ? (
                            <span style={{ fontSize: 12, color: "#f59e0b", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
                              <ClockCircleOutlined />
                              Est. {formatDuration(gapSeconds)} to complete
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 500 }}>Sends in one batch</span>
                          );
                        })()}
                      </div>

                      {/* Detected columns */}
                      {excelColumns.length > 0 && (
                        <div style={{ marginTop: 10, padding: "8px 12px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#15803d" }}>Detected columns: </span>
                          {excelColumns.map((col) => (
                            <span key={col} style={{
                              display: "inline-block", margin: "2px 4px",
                              background: "#dcfce7", border: "1px solid #86efac",
                              borderRadius: 4, padding: "1px 7px",
                              fontSize: 11, fontFamily: "monospace", color: "#166534",
                            }}>{col}</span>
                          ))}
                          <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>
                            Use <code style={{ background: "#f3f4f6", padding: "0 3px", borderRadius: 3 }}>{`{{columnName}}`}</code> to personalise each email.
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {target === "specific" && (
              <Form.Item
                label="Select Companies"
                name="targetCompanyIds"
                rules={[{ required: true, message: "Please select at least one company" }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder={companiesLoading ? "Loading companies…" : "Search and select companies"}
                  loading={companiesLoading}
                  notFoundContent={companiesLoading ? <Spin size="small" /> : "No companies found"}
                  filterOption={(input, opt) => (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  maxTagCount="responsive"
                  options={companyOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}

            <Form.Item
              label="Reply-To Email (optional)"
              name="replyTo"
              extra="Recipients who reply will send their message to this address instead of the From address"
            >
              <Input placeholder="e.g. support@voycell.com" type="email" />
            </Form.Item>


            <Form.Item
              label="Email Subject"
              name="subject"
              rules={[{ required: true, message: "Subject is required" }]}
            >
              <Input placeholder="e.g. Important Update from VOYCELL" maxLength={150} showCount />
            </Form.Item>

            {/* Heading now accepts HTML */}
            <Form.Item
              label="Email Heading"
              name="title"
              extra="Supports HTML — shown as the large heading inside the email body"
            >
              <TextArea
                rows={2}
                placeholder='e.g. System Maintenance on Sunday  or  <span style="color:#e53e3e">🚨 Urgent Notice</span>'
              />
            </Form.Item>

            {/* ── Supplemental Excel merge (only visible when recipients bulk-loaded) ── */}
            {bulkLoadedRecipients !== null && (
              <div style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#15803d" }}>
                    ➕ Add Missing Data Columns (optional)
                  </span>
                  <Button
                    size="small"
                    style={{ color: "#6b7280" }}
                    onClick={() => { setBulkLoadedRecipients(null); setBulkLoadedFields([]); setSuppMergeResult(null); }}
                  >
                    Dismiss
                  </Button>
                </div>
                <p style={{ fontSize: 12, color: "#166534", margin: "0 0 10px" }}>
                  Recipients were loaded from previous campaigns. If you need a new data column (e.g. <code>phone</code>)
                  that wasn't in the original campaigns, upload an Excel with <strong>email</strong> + new columns only.
                  Rows are matched by email and merged. Recipients not found keep their original data.
                </p>
                <Upload
                  accept=".xlsx,.xls,.csv"
                  showUploadList={false}
                  beforeUpload={handleSuppExcelUpload}
                >
                  <Button
                    size="small"
                    icon={suppExcelParsing ? <Spin size="small" /> : <UploadOutlined />}
                    loading={suppExcelParsing}
                    style={{ borderRadius: 6 }}
                  >
                    Upload Supplemental Excel
                  </Button>
                </Upload>
                {suppMergeResult && !suppMergeResult.error && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#166534" }}>
                    ✅ Merged: <strong>{suppMergeResult.matched}</strong> matched,{" "}
                    <strong>{suppMergeResult.unmatched}</strong> unmatched (sent with empty fields).
                    {suppMergeResult.newColumns?.length > 0 && (
                      <> New columns added: {suppMergeResult.newColumns.map((c) => <code key={c} style={{ marginLeft: 4 }}>{`{{${c}}}`}</code>)}</>
                    )}
                  </div>
                )}
                {suppMergeResult?.error && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626" }}>❌ {suppMergeResult.error}</div>
                )}
              </div>
            )}

            {/* ── Dynamic Fields panel ─────────────────────────────────── */}
            <div style={{
              background: "#f8faff",
              border: "1px solid #dbeafe",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 20,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: "#1e40af" }}>
                  ⚡ Dynamic Fields
                </span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Click a field to insert it at the cursor in the body
                </span>
              </div>

              {/* Existing fields */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: dynamicFields.length ? 12 : 0 }}>
                {dynamicFields.map((field) => (
                  <span
                    key={field}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "#dbeafe", border: "1px solid #93c5fd",
                      borderRadius: 6, padding: "3px 10px", fontSize: 13,
                      fontFamily: "monospace", color: "#1d4ed8",
                    }}
                  >
                    <span
                      onClick={() => insertFieldAtCursor(field)}
                      style={{ cursor: "pointer" }}
                      title="Click to insert in body"
                    >
                      {`{{${field}}}`}
                    </span>
                    <span
                      onClick={() => removeDynamicField(field)}
                      style={{ cursor: "pointer", color: "#93c5fd", fontWeight: 700, fontSize: 15, lineHeight: 1 }}
                      title="Remove field"
                    >
                      ×
                    </span>
                  </span>
                ))}
                {dynamicFields.length === 0 && (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    No fields yet. Add one below.
                  </span>
                )}
              </div>

              {/* Add new field */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Input
                  size="small"
                  placeholder="Field name e.g. name, company, phone"
                  value={newFieldInput}
                  onChange={(e) => setNewFieldInput(e.target.value)}
                  onPressEnter={addDynamicField}
                  style={{ maxWidth: 260, fontFamily: "monospace", fontSize: 12 }}
                  prefix={<span style={{ color: "#93c5fd", fontSize: 11 }}>{"{{"}</span>}
                  suffix={<span style={{ color: "#93c5fd", fontSize: 11 }}>{"}}"}</span>}
                />
                <Button size="small" type="primary" onClick={addDynamicField} icon={<PlusOutlined />}>
                  Add Field
                </Button>
              </div>

              {dynamicFields.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280" }}>
                  💡 For <strong>Excel</strong> mode: your Excel must have column headers matching these field names exactly.
                  For other modes: <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{company}}"}</code> are auto-filled from the user profile.
                </div>
              )}
            </div>

            <Form.Item
              label={
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  Email Body
                  <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewOpen(true)}
                    style={{ fontWeight: 500, borderRadius: 6 }}
                  >
                    Preview
                  </Button>
                </span>
              }
              name="body"
              rules={[{ required: true, message: "Body is required" }]}
              extra="Supports HTML. Click Preview to see how it looks in the actual email."
            >
              <TextArea
                ref={bodyTextareaRef}
                rows={8}
                placeholder="Write your email content here. You can use HTML tags like <b>, <p>, <a>, <ul>…"
              />
            </Form.Item>

            {/* ── Attachments ── */}
            <Form.Item label="Attachments (optional)" extra="Max 5 files, 10 MB each">
              <Upload
                multiple
                maxCount={5}
                showUploadList={false}
                beforeUpload={(file) => {
                  setAttachmentFiles((prev) => [...prev, file]);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>Add Attachment</Button>
              </Upload>
              {/* Custom file list — always-visible red delete on the left */}
              {attachmentFiles.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {attachmentFiles.map((f) => (
                    <div key={f.uid || f.name} style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 10px", background: "#f9fafb",
                      border: "1px solid #e5e7eb", borderRadius: 8,
                    }}>
                      <DeleteOutlined
                        onClick={() => setAttachmentFiles((prev) => prev.filter((x) => (x.uid || x.name) !== (f.uid || f.name)))}
                        style={{ color: "#ef4444", cursor: "pointer", fontSize: 14, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 13, color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Form.Item>

            {(result || excelResult) && (
              <Alert
                type={(result || excelResult).type}
                message={(result || excelResult).message}
                showIcon closable
                onClose={() => { setResult(null); setExcelResult(null); }}
                style={{ marginBottom: 16 }}
              />
            )}

            {target === "excel" && batchConfig?.enabled && (() => {
              const totalSel = ["company","normal","suspicious"].reduce(
                (sum, k) => sum + (selectedCategories[k] ? emailCategories[k].length : 0), 0
              );
              if (totalSel === 0) return null;
              const batchCount = Math.ceil(totalSel / batchConfig.batchSize);
              const gapSeconds = (batchCount - 1) * batchConfig.intervalSeconds;
              if (gapSeconds <= 0) return null;
              const startTime  = scheduleMode === "scheduled" && scheduleAt ? scheduleAt : dayjs();
              const completesAt = startTime.add(gapSeconds, "second");
              return (
                <div style={{ marginBottom: 12, padding: "10px 16px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 8 }}>
                  <ClockCircleOutlined style={{ color: "#d97706" }} />
                  <span style={{ fontSize: 13, color: "#92400e" }}>
                    Estimated completion:&nbsp;
                    <strong>{completesAt.format("ddd, D MMM YYYY [at] h:mm A")}</strong>
                    &nbsp;<span style={{ color: "#b45309" }}>({formatDuration(gapSeconds)})</span>
                  </span>
                </div>
              );
            })()}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Button
                size="large"
                style={{ borderRadius: 8, minWidth: 100 }}
                onClick={() => {
                  form.resetFields();
                  setTarget("all");
                  setResult(null);
                  setExcelResult(null);
                  setExcelValidEmails([]);
                  setExcelInvalidEmails([]);
                  setExcelDuplicateEmails([]);
                  setExcelFileName("");
                  setManualEmailInput("");
                  setAttachmentFiles([]);
                  setBulkLoadedRecipients(null);
                  setBulkLoadedFields([]);
                  setSuppMergeResult(null);
                }}
              >
                Reset
              </Button>
              {/* ── Scheduler ── */}
              <div style={{
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                padding: "10px 14px", background: "#f9fafb",
                border: "1px solid #e5e7eb", borderRadius: 8,
              }}>
                <ClockCircleOutlined style={{ color: "#6366f1" }} />
                <Radio.Group
                  value={scheduleMode}
                  onChange={(e) => { setScheduleMode(e.target.value); if (e.target.value === "immediate") setScheduleAt(null); }}
                  size="small"
                >
                  <Radio value="immediate">Send immediately</Radio>
                  <Radio value="scheduled">Schedule for</Radio>
                </Radio.Group>
                {scheduleMode === "scheduled" && (
                  <DatePicker
                    showTime={{ use12Hours: true, format: "hh:mm A" }}
                    size="small"
                    format="DD MMM YYYY hh:mm A"
                    value={scheduleAt}
                    onChange={setScheduleAt}
                    disabledDate={(d) => d && d.isBefore(dayjs(), "minute")}
                    placeholder="Pick date & time"
                    style={{ borderRadius: 6 }}
                  />
                )}
              </div>

              {target === "excel" ? (() => {
                const totalSelected = ["company","normal","suspicious"].reduce(
                  (sum, k) => sum + (selectedCategories[k] ? emailCategories[k].length : 0), 0
                );
                return (
                <Button
                  type="primary"
                  loading={excelSending}
                  icon={scheduleMode === "scheduled" ? <ClockCircleOutlined /> : <MailOutlined />}
                  size="large"
                  disabled={totalSelected === 0 || (scheduleMode === "scheduled" && !scheduleAt)}
                  style={{ background: "#16a34a", borderColor: "#16a34a", minWidth: 200 }}
                  onClick={handleExcelSend}
                >
                  {excelSending ? "Sending…" : totalSelected > 0
                    ? (scheduleMode === "scheduled" ? `Schedule ${totalSelected} Email${totalSelected !== 1 ? "s" : ""}` : `Send to ${totalSelected} Email${totalSelected !== 1 ? "s" : ""}`)
                    : "Select a category to send"}
                </Button>
                );
              })() : (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={scheduleMode === "scheduled" ? <ClockCircleOutlined /> : <MailOutlined />}
                  size="large"
                  disabled={scheduleMode === "scheduled" && !scheduleAt}
                  style={{ background: scheduleMode === "scheduled" ? "#6366f1" : "#6366f1", borderColor: "#6366f1", minWidth: 160 }}
                >
                  {submitting ? "Sending…" : scheduleMode === "scheduled" ? "Schedule Email" : "Send Email"}
                </Button>
              )}
            </div>
          </Form>
        </Card>

        {/* ── Sent History ── */}
        <Divider style={{ marginBottom: 24 }} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h5 style={{ fontWeight: 700, marginBottom: 2 }}>
              <MailOutlined style={{ marginRight: 8, color: "#6366f1" }} />
              Sent Email History
            </h5>
            <p style={{ color: "#6b7280", margin: 0, fontSize: 13 }}>
              {logsTotal} broadcast{logsTotal !== 1 ? "s" : ""} sent
            </p>
          </div>
          <Button
            icon={<ReloadOutlined />}
            style={{ borderRadius: 8 }}
            onClick={() => { loadLogs(logsPage); loadBatchJobs(); }}
            loading={logsLoading}
          >
            Refresh
          </Button>
        </div>

        {/* ── Active batch jobs (non-paginated) ── */}
        {batchData.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <span>
                <ClockCircleOutlined style={{ marginRight: 6, color: "#f59e0b" }} />
                Active Batch Jobs ({batchData.length})
              </span>
              <Tooltip title="Fix jobs stuck as IN_PROGRESS where all batches are done but status never finalized">
                <Button
                  size="small"
                  icon={<WarningOutlined />}
                  loading={repairingJobs}
                  onClick={handleRepairStuckJobs}
                  style={{ borderRadius: 8, borderColor: "#f59e0b", color: "#f59e0b", fontSize: 12 }}
                >
                  Repair Stuck Jobs
                </Button>
              </Tooltip>
            </div>
            <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
              <Table
                dataSource={batchData}
                columns={logColumns}
                rowKey="_rowKey"
                rowClassName={() => "batch-row"}
                loading={batchJobsLoading}
                pagination={false}
                size="small"
              />
            </Card>
          </div>
        )}

        {/* ── Bulk action bar ── */}
        {selectedRowKeys.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
            padding: "10px 16px", marginBottom: 10,
            background: "#eef2ff", borderRadius: 10, border: "1px solid #c7d2fe",
          }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: "#4338ca", marginRight: 4 }}>
              {selectedRowKeys.length} broadcast{selectedRowKeys.length !== 1 ? "s" : ""} selected
            </span>
            <Button size="small" icon={<DownloadOutlined />} loading={bulkDownloading} onClick={() => handleBulkDownload("delivered")} style={{ borderColor: "#86efac", color: "#15803d" }}>
              Download Delivered
            </Button>
            <Button size="small" icon={<DownloadOutlined />} loading={bulkDownloading} onClick={() => handleBulkDownload("opened")} style={{ borderColor: "#93c5fd", color: "#1d4ed8" }}>
              Download Opened
            </Button>
            <Button size="small" icon={<DownloadOutlined />} loading={bulkDownloading} onClick={() => handleBulkDownload("bounced")} style={{ borderColor: "#fca5a5", color: "#dc2626" }}>
              Download Bounced
            </Button>
            <Button size="small" icon={<DownloadOutlined />} loading={bulkDownloading} onClick={() => handleBulkDownload("undelivered")} style={{ borderColor: "#d1d5db", color: "#6b7280" }}>
              Download No Status
            </Button>
            <Button size="small" icon={<DownloadOutlined />} loading={bulkDownloading} onClick={() => handleBulkDownload("all")} style={{ borderColor: "#d1d5db", color: "#374151" }}>
              Download All Recipients
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleBulkExportStats} style={{ borderColor: "#c4b5fd", color: "#7c3aed" }}>
              Export Stats
            </Button>
            <Divider type="vertical" style={{ margin: "0 4px" }} />
            <Tooltip title="Resend to opened recipients — each campaign uses its original sender">
              <Button size="small" icon={<SendOutlined />}
                onClick={() => { setBulkResendSenderId(selectedSenderId); setBulkSenderMode("original"); setBulkResendFilter("opened"); setBulkResendResults([]); resendForm.resetFields(); setBulkResendModal(true); }}
                style={{ borderColor: "#6ee7b7", color: "#065f46", fontWeight: 600 }}>
                Resend Opened (original senders)
              </Button>
            </Tooltip>
            <Tooltip title="Resend to opened recipients from all selected campaigns using one single sender">
              <Button size="small" icon={<SendOutlined />}
                onClick={() => { setBulkResendSenderId(selectedSenderId); setBulkSenderMode("single"); setBulkResendFilter("opened"); setBulkResendResults([]); resendForm.resetFields(); setBulkResendModal(true); }}
                style={{ borderColor: "#93c5fd", color: "#1d4ed8", fontWeight: 600 }}>
                Resend Opened (single sender)
              </Button>
            </Tooltip>
            <Tooltip title="Resend to delivered recipients — each campaign uses its original sender">
              <Button size="small" icon={<SendOutlined />}
                onClick={() => { setBulkResendSenderId(selectedSenderId); setBulkSenderMode("original"); setBulkResendFilter("delivered"); setBulkResendResults([]); resendForm.resetFields(); setBulkResendModal(true); }}
                style={{ borderColor: "#86efac", color: "#15803d", fontWeight: 600 }}>
                Resend Delivered (original senders)
              </Button>
            </Tooltip>
            <Tooltip title="Resend to delivered recipients from all selected campaigns using one single sender">
              <Button size="small" icon={<SendOutlined />}
                onClick={() => { setBulkResendSenderId(selectedSenderId); setBulkSenderMode("single"); setBulkResendFilter("delivered"); setBulkResendResults([]); resendForm.resetFields(); setBulkResendModal(true); }}
                style={{ borderColor: "#7dd3fc", color: "#0369a1", fontWeight: 600 }}>
                Resend Delivered (single sender)
              </Button>
            </Tooltip>
            <Divider type="vertical" style={{ margin: "0 4px" }} />
            <Tooltip title="Fetch all recipients across selected campaigns (deduplicated) and pre-load into compose form">
              <Button size="small" icon={<MailOutlined />}
                loading={bulkTargeting === "all"}
                onClick={() => handleBulkTarget("all")}
                style={{ borderColor: "#374151", color: "#374151", fontWeight: 600 }}>
                Send to All (excl. Bounced)
              </Button>
            </Tooltip>
            <Button size="small" onClick={() => { setSelectedRowKeys([]); setSelectedRowsMap({}); }} style={{ marginLeft: "auto", color: "#6b7280" }}>
              Clear
            </Button>
          </div>
        )}

        {/* ── Paginated email log history ── */}
        <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
          <Table
            dataSource={combinedData}
            columns={logColumns}
            rowKey="_rowKey"
            loading={logsLoading}
            scroll={{ x: "max-content", y: 600 }}
            rowSelection={{
              selectedRowKeys,
              onChange: (newKeys, currentPageSelected) => {
                setSelectedRowKeys(newKeys);
                setSelectedRowsMap((prev) => {
                  const next = { ...prev };
                  currentPageSelected.forEach((r) => { next[r._rowKey] = r; });
                  combinedData.forEach((r) => {
                    if (!newKeys.includes(r._rowKey)) delete next[r._rowKey];
                  });
                  return next;
                });
              },
            }}
            pagination={{
              current:   logsPage,
              total:     logsTotal,
              pageSize:  logsLimit,
              onChange:  (p, ps) => {
                if (ps !== logsLimit) {
                  setLogsLimit(ps);
                  loadLogs(1, ps);
                } else {
                  loadLogs(p, ps);
                }
              },
              onShowSizeChange: (_, ps) => {
                setLogsLimit(ps);
                loadLogs(1, ps);
              },
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100","500"],
              showTotal: (t, [from, to]) => `${from}–${to} of ${t}`,
              size:      "small",
              style:     { padding: "12px 16px" },
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <MailOutlined style={{ fontSize: 36, color: "#d1d5db", display: "block", marginBottom: 12 }} />
                  <p style={{ color: "#9ca3af", marginBottom: 0 }}>No emails sent yet</p>
                  <p style={{ color: "#d1d5db", fontSize: 12 }}>Sent emails will appear here</p>
                </div>
              ),
            }}
          />
        </Card>

        {/* Detail modal — regular email logs */}
        <EmailDetailModal
          log={viewLog}
          onClose={() => setViewLog(null)}
          onUseAsTemplate={handleUseAsTemplate}
          onStatsRefreshed={() => loadLogs(logsPage)}
          onTargetRecipients={handleTargetRecipients}
        />

        {/* Batch job detail modal */}
        <BatchDetailModal
          job={viewBatch}
          onClose={() => setViewBatch(null)}
          onUseAsTemplate={handleUseAsTemplate}
        />

        {/* ── Email Preview Modal ─────────────────────────────────────────────── */}
        <Modal
          open={previewOpen}
          onCancel={() => setPreviewOpen(false)}
          title={<span><EyeOutlined style={{ marginRight: 8 }} />Email Preview</span>}
          width={700}
          footer={<Button onClick={() => setPreviewOpen(false)}>Close</Button>}
          destroyOnClose
        >
          {(() => {
            const vals    = form.getFieldsValue();
            const subject = vals.subject || "";
            const title   = vals.title   || "";
            const body    = vals.body    || "";
            const html    = buildPreviewHtml(subject, title, body);
            return (
              <>
                <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }}>
                  <span style={{ color: "#6b7280" }}>Subject: </span>
                  <strong>{subject || <em style={{ color: "#aaa" }}>not set</em>}</strong>
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  <HtmlPreview html={html} />
                </div>
                {dynamicFields.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                    💡 Dynamic placeholders like <code>{`{{name}}`}</code> are shown as-is in preview — they get replaced with real values at send time.
                  </div>
                )}
              </>
            );
          })()}
        </Modal>

        {/* ── Bulk Resend to Opened Modal ─────────────────────────────────────── */}
        <Modal
          open={bulkResendModal}
          onCancel={() => { setBulkResendModal(false); resendForm.resetFields(); setBulkResendResults([]); setResendScheduleMode("immediate"); setResendScheduleAt(null); setResendSuppMap({}); setResendDynamicFields([]); setResendSuppResult(null); }}
          title={
            <span>
              <SendOutlined style={{ marginRight: 8, color: "#6366f1" }} />
              Resend to {bulkResendFilter === "opened" ? "Opened" : "Delivered"} —{" "}
              <span style={{ color: bulkSenderMode === "original" ? "#065f46" : "#1d4ed8", fontSize: 13 }}>
                {bulkSenderMode === "original" ? "original senders" : "single sender"}
              </span>
            </span>
          }
          width={780}
          destroyOnClose
          footer={
            bulkResendResults.length > 0 ? (
              <Button onClick={() => { setBulkResendModal(false); resendForm.resetFields(); setBulkResendResults([]); setResendScheduleMode("immediate"); setResendScheduleAt(null); setResendSuppMap({}); setResendDynamicFields([]); setResendSuppResult(null); }}>Close</Button>
            ) : (() => {
                const logRows        = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");
                const estimatedTotal = logRows.reduce((sum, r) => {
                  const c = bulkResendFilter === "opened"
                    ? (r.stats?.opens || 0)
                    : (r.stats?.netDeliveries ?? r.stats?.deliveries ?? 0);
                  return sum + c;
                }, 0);
                const btnLabel = resendScheduleMode === "scheduled"
                  ? `Schedule ${estimatedTotal} Email${estimatedTotal !== 1 ? "s" : ""}`
                  : `Send to ${estimatedTotal} Email${estimatedTotal !== 1 ? "s" : ""}`;
                return (
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={() => { setBulkResendModal(false); resendForm.resetFields(); setResendScheduleMode("immediate"); setResendScheduleAt(null); setResendSuppMap({}); setResendDynamicFields([]); setResendSuppResult(null); }}>Cancel</Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={bulkResending}
                      onClick={handleBulkResendOpened}
                      disabled={resendScheduleMode === "scheduled" && !resendScheduleAt}
                      style={{ background: "#16a34a", borderColor: "#16a34a" }}
                    >
                      {btnLabel}
                    </Button>
                  </div>
                );
              })()
          }
        >
          {/* Results section — shown after send completes */}
          {bulkResendResults.length > 0 ? (
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 10 }}>Send Results</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {bulkResendResults.map((r, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "8px 12px", borderRadius: 8, fontSize: 12,
                    background: r.status === "success" ? "#f0fdf4" : r.status === "error" ? "#fef2f2" : "#f9fafb",
                    border: `1px solid ${r.status === "success" ? "#bbf7d0" : r.status === "error" ? "#fecaca" : "#e5e7eb"}`,
                  }}>
                    {r.status === "success" && <CheckCircleOutlined style={{ color: "#16a34a" }} />}
                    {r.status === "error"   && <CloseCircleOutlined style={{ color: "#dc2626" }} />}
                    {r.status === "skipped" && <MinusCircleOutlined style={{ color: "#9ca3af" }} />}
                    <span style={{ flex: 1, fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.subject}
                    </span>
                    {r.sender && <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>{r.sender}</span>}
                    {r.matchedSender === false && <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>used default sender</Tag>}
                    <span style={{ fontWeight: 600, whiteSpace: "nowrap", color: r.status === "success" ? "#15803d" : r.status === "error" ? "#dc2626" : "#6b7280" }}>
                      {r.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Campaign summary */}
              {(() => {
                const logRows = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");
                return (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 8 }}>
                      Selected campaigns ({logRows.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {logRows.map((r) => {
                        const count = bulkResendFilter === "opened"
                          ? (r.stats?.opens || 0)
                          : (r.stats?.netDeliveries ?? r.stats?.deliveries ?? 0);
                        const countLabel = bulkResendFilter === "opened" ? "opened" : "delivered";
                        const matched     = senders.find((s) => s.email === r.fromEmail);
                        return (
                          <div key={r._rowKey} style={{
                            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                            padding: "7px 12px", background: "#f9fafb", borderRadius: 8,
                            border: "1px solid #e5e7eb", fontSize: 12,
                          }}>
                            <span style={{ flex: 1, fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                              {r.subject || "(no subject)"}
                            </span>
                            <span style={{ color: count > 0 ? (bulkResendFilter === "opened" ? "#1d4ed8" : "#15803d") : "#9ca3af", fontWeight: 600, whiteSpace: "nowrap" }}>
                              {count} {countLabel}
                            </span>
                            {r.fromEmail && (
                              <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                                from: {r.fromEmail}
                              </span>
                            )}
                            {bulkSenderMode === "original" && (
                              matched
                                ? <Tag color="green"  style={{ margin: 0, fontSize: 11 }}>✓ sender matched</Tag>
                                : r.fromEmail
                                  ? <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>⚠ not found → default</Tag>
                                  : <Tag color="default" style={{ margin: 0, fontSize: 11 }}>default</Tag>
                            )}
                            {count === 0 && <Tag color="default" style={{ margin: 0, fontSize: 11 }}>will skip</Tag>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Sender section */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 8 }}>
                  {bulkSenderMode === "original"
                    ? "Sending from: original sender per campaign"
                    : "Select sender"}
                </div>
                {bulkSenderMode === "original" ? (
                  <div style={{ fontSize: 12, color: "#6b7280", padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                    Each campaign will be sent from its original sender email. Campaigns whose original sender is no longer in your senders list will use the default sender.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {senders.map((s) => (
                      <div
                        key={s._id}
                        onClick={() => setBulkResendSenderId(s._id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                          border: `2px solid ${bulkResendSenderId === s._id ? "#6366f1" : "#e5e7eb"}`,
                          background: bulkResendSenderId === s._id ? "#ede9fe" : "#fff",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#111827" }}>{s.name}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>{s.email}</div>
                        </div>
                        {s.isDefault && <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>Default</Tag>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Load from history template */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 6 }}>
                  Load content from past email <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                </div>
                <Select
                  placeholder="Pick a past campaign to pre-fill subject & body…"
                  style={{ width: "100%" }}
                  loading={bulkResendTemplateLoading}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={logs.map((l) => ({ value: l._id, label: l.subject || "(no subject)" }))}
                  onChange={async (logId) => {
                    if (!logId) return;
                    setBulkResendTemplateLoading(true);
                    try {
                      const res = await api.get(`/notifications/email-logs/${logId}`);
                      const d = res.data?.data;
                      if (d) resendForm.setFieldsValue({ subject: d.subject || "", title: d.title || "", body: d.body || "" });
                    } catch {}
                    finally { setBulkResendTemplateLoading(false); }
                  }}
                />
              </div>

              {/* Supplemental Excel for dynamic fields */}
              <div style={{ marginBottom: 16, padding: "12px 14px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#15803d", marginBottom: 6 }}>
                  ➕ Add Dynamic Fields via Excel <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional)</span>
                </div>
                <p style={{ fontSize: 12, color: "#166534", margin: "0 0 10px" }}>
                  Upload an Excel with <strong>email</strong> + extra columns (e.g. <code>phone</code>, <code>company</code>).
                  Matched by email — use <code>{`{{column_name}}`}</code> in body for personalisation.
                </p>
                <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleResendSuppExcel}>
                  <Button size="small" icon={resendSuppParsing ? <Spin size="small" /> : <UploadOutlined />} loading={resendSuppParsing}>
                    Upload Excel
                  </Button>
                </Upload>
                {resendSuppResult && !resendSuppResult.error && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#166534" }}>
                    ✅ Loaded <strong>{resendSuppResult.total}</strong> rows.
                    {resendSuppResult.newColumns?.length > 0 && (
                      <> Fields: {resendSuppResult.newColumns.map((c) => <code key={c} style={{ marginLeft: 4 }}>{`{{${c}}}`}</code>)}</>
                    )}
                  </div>
                )}
                {resendSuppResult?.error && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626" }}>❌ {resendSuppResult.error}</div>
                )}
              </div>

              {/* Compose fields */}
              <Form form={resendForm} layout="vertical">
                <Form.Item
                  label="Subject"
                  name="subject"
                  rules={[{ required: true, message: "Subject is required" }]}
                >
                  <Input placeholder="e.g. Following up on your interest" maxLength={150} showCount />
                </Form.Item>
                <Form.Item label="Heading (optional)" name="title">
                  <TextArea rows={2} placeholder="Email heading — supports HTML" />
                </Form.Item>
                <Form.Item
                  label="Body"
                  name="body"
                  rules={[{ required: true, message: "Body is required" }]}
                  extra="Supports HTML tags like <b>, <p>, <a>, <ul>…"
                >
                  <TextArea rows={8} placeholder="Write your email content here. You can use HTML tags." />
                </Form.Item>
              </Form>

              {/* Scheduling */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 8 }}>
                <ClockCircleOutlined style={{ color: "#6366f1" }} />
                <Radio.Group
                  value={resendScheduleMode}
                  onChange={(e) => { setResendScheduleMode(e.target.value); if (e.target.value === "immediate") setResendScheduleAt(null); }}
                  size="small"
                >
                  <Radio value="immediate">Send immediately</Radio>
                  <Radio value="scheduled">Schedule for</Radio>
                </Radio.Group>
                {resendScheduleMode === "scheduled" && (
                  <DatePicker
                    showTime={{ use12Hours: true, format: "hh:mm A" }}
                    size="small"
                    format="DD MMM YYYY hh:mm A"
                    value={resendScheduleAt}
                    onChange={setResendScheduleAt}
                    disabledDate={(d) => d && d.isBefore(dayjs(), "minute")}
                    placeholder="Pick date & time"
                    style={{ borderRadius: 6 }}
                  />
                )}
              </div>

              {/* Estimated completion banner */}
              {batchConfig?.enabled && (() => {
                const logRows        = Object.values(selectedRowsMap).filter((r) => r._rowType === "log");
                const estimatedTotal = logRows.reduce((sum, r) => {
                  const c = bulkResendFilter === "opened"
                    ? (r.stats?.opens || 0)
                    : (r.stats?.netDeliveries ?? r.stats?.deliveries ?? 0);
                  return sum + c;
                }, 0);
                if (estimatedTotal === 0) return null;
                const batchCount = Math.ceil(estimatedTotal / batchConfig.batchSize);
                const gapSeconds = (batchCount - 1) * batchConfig.intervalSeconds;
                if (gapSeconds <= 0) return null;
                const startTime   = resendScheduleMode === "scheduled" && resendScheduleAt ? resendScheduleAt : dayjs();
                const completesAt = startTime.add(gapSeconds, "second");
                return (
                  <div style={{ marginTop: 8, padding: "10px 14px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 8 }}>
                    <ClockCircleOutlined style={{ color: "#d97706" }} />
                    <span style={{ fontSize: 13, color: "#92400e" }}>
                      Estimated completion:&nbsp;
                      <strong>{completesAt.format("ddd, D MMM YYYY [at] h:mm A")}</strong>
                      &nbsp;<span style={{ color: "#b45309" }}>({formatDuration(gapSeconds)})</span>
                    </span>
                  </div>
                );
              })()}

              {/* Batching note */}
              <div style={{ fontSize: 12, color: "#6b7280", padding: "6px 10px", marginTop: 8, background: "#f8faff", borderRadius: 6, border: "1px solid #dbeafe" }}>
                ⚙️ Batching follows your global Batch Configuration (batch size &amp; interval). Emails are sent in batches automatically.
              </div>
            </>
          )}
        </Modal>

      </div>
    </div>
  );
}
